import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Filter, Info, Zap, Wifi, Cpu, Edit3, GitCompare, Code, Copy, ShoppingCart, FileText, Check, Download, Share2, ExternalLink, BookOpen, Compass, Link2 } from 'lucide-react';
import { adsAPI, boardsAPI } from '../services/api';
import BoardPublicSvgPreview from '../components/BoardPublicSvgPreview';
import CompareModal from '../components/CompareModal';
import { richContentBodyStyles } from '../components/richContentBodyStyles';
import { appUrl } from '../utils/appPaths';
import { buildBoardPinoutRasterPreviewSrc, buildBoardSvgPreviewSrc } from '../utils/boardSvgPreview';
import { PUBLIC_CONTENT_PAGE_MAX_WIDTH } from '../utils/publicPageLayout';
import {
  deriveBoardSeoDescription,
  deriveBoardSeoKeywords,
  stripHtmlToText,
  usePageSeo,
  useStructuredData,
} from '../utils/seo';
import {
  getBoardGalleryImageCaption,
  normalizeBoardGalleryImages,
  resolveBoardGalleryImageSrc
} from '../utils/boardGallery';
import { normalizeAffiliateLinks } from '../utils/affiliateLinks';
import { normalizeTutorialUrls } from '../utils/tutorialUrls';
import {
  formatBoardVoltage,
  formatPinCurrent,
  formatPinVoltage
} from '../utils/compatibilityDisplay';
import {
  getBoardVerificationLabel,
  normalizeStringArray,
  normalizeSupportedProtocols,
  normalizeBoardVerificationStatus,
  PROTOCOL_OPTIONS,
  withPinCompatibilityDefaults,
} from '../utils/compatibilityOptions';
import { downloadSvgElementAsPng } from '../utils/svgExport';
import {
  buildPinoutPreviewSvg,
  getCalloutLayoutKey,
  normalizeRotation,
  rotateCallouts
} from '../utils/pinCalloutLayout';
import {
  normalizeSvgViewBox,
  parseSvgViewBoxFromMarkup,
  rotateSvgViewBox
} from '../utils/svgViewBox';
import {
  getPinFilterGroupDefinition,
  getPinCapabilityTags,
  getPinFilterGroups,
  getPinFilterMatchState,
  normalizePinFilterGroup,
  normalizePinFilterGroupList,
  PIN_CAPABILITY_COLORS,
  PIN_FILTER_GROUPS,
} from '../utils/pinFilterGroups';
import { readBoardPreviewDraft } from '../utils/boardPreviewDraft';
import { AD_SLOT_BOARD_DETAIL_SIDEBAR, findAdRecordBySlot } from '../utils/adSlots';
import { getDefaultPublicPinCalloutColor } from '../utils/publicPinoutCalloutColors';

const PROTOCOL_LABELS = Object.fromEntries(
  PROTOCOL_OPTIONS.map(({ value, label }) => [value, label])
);

const PROTOCOL_SORT_ORDER = Object.fromEntries(
  PROTOCOL_OPTIONS.map(({ value }, index) => [value, index])
);

const BOARD_DETAIL_LAYOUT_MAX_WIDTH = `calc(${PUBLIC_CONTENT_PAGE_MAX_WIDTH} + 132px)`;
const BOARD_DETAIL_PREVIEW_MAX_WIDTH = '920px';

const formatSupportedProtocolLabel = (protocol) => {
  const normalizedProtocol = String(protocol ?? '').trim().toLowerCase();

  if (!normalizedProtocol) {
    return '';
  }

  return PROTOCOL_LABELS[normalizedProtocol] || String(protocol).trim();
};

const SIMILAR_BOARD_REASON_LABELS = {
  'the same board category': 'Same board category',
  'the same package type': 'Same package type',
  'the same pin count': 'Same pin count',
  'a very similar pin count': 'Similar pin count',
  'a similar pin count': 'Similar pin count',
  'a similar clock speed': 'Similar clock speed',
  'a similar flash size': 'Similar flash size',
  'a similar ram size': 'Similar RAM',
  'the arduino-compatible ecosystem': 'Arduino ecosystem',
  'the avr-based board family': 'AVR family',
  'the esp32-based board family': 'ESP32 family',
  'the esp8266 wi-fi board family': 'ESP8266 family',
  'the rp2040-based board family': 'RP2040 family',
  'the stm32-based board family': 'STM32 family',
  'the feather ecosystem': 'Feather ecosystem',
  'the teensy ecosystem': 'Teensy ecosystem',
  'wireless features': 'Wireless',
  'bluetooth support': 'Bluetooth',
  'native usb support': 'Native USB',
  'a nano-style form factor': 'Nano-style form factor',
  'a uno-style form factor': 'Uno-style form factor',
  'a compact mini form factor': 'Compact mini form factor',
  'a pico-style form factor': 'Pico-style form factor',
  'a breadboard-friendly layout': 'Breadboard-friendly layout',
};

const SIMILAR_BOARD_REASON_PART_PATTERNS = [
  /the same board category/gi,
  /the same package type/gi,
  /the same [^,]+? CPU/gi,
  /the same [^,]+? logic level/gi,
  /the same pin count/gi,
  /a very similar pin count/gi,
  /a similar pin count/gi,
  /similar [^.]+? capabilities/gi,
  /the Arduino-compatible ecosystem/gi,
  /the AVR-based board family/gi,
  /the ESP32-based board family/gi,
  /the ESP8266 Wi-Fi board family/gi,
  /the RP2040-based board family/gi,
  /the STM32-based board family/gi,
  /the Feather ecosystem/gi,
  /the Teensy ecosystem/gi,
  /wireless features/gi,
  /Bluetooth support/gi,
  /native USB support/gi,
  /a Nano-style form factor/gi,
  /a Uno-style form factor/gi,
  /a compact mini form factor/gi,
  /a Pico-style form factor/gi,
  /a breadboard-friendly layout/gi,
  /a similar clock speed/gi,
  /a similar flash size/gi,
  /a similar RAM size/gi,
];

const toTitleLikeText = (value) => {
  const normalizedValue = String(value ?? '').trim();
  if (!normalizedValue) {
    return '';
  }

  return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
};

const normalizeSimilarBoardCapabilityList = (value) => (
  String(value ?? '')
    .replace(/,\s*and\s+/gi, ', ')
    .replace(/\s+and\s+/gi, ', ')
    .replace(/\s*,\s*/g, ', ')
    .trim()
);

const formatSimilarBoardReasonPart = (value) => {
  const normalizedValue = String(value ?? '').trim().replace(/\.$/, '');
  const normalizedKey = normalizedValue.toLowerCase();

  if (!normalizedValue) {
    return '';
  }

  if (SIMILAR_BOARD_REASON_LABELS[normalizedKey]) {
    return SIMILAR_BOARD_REASON_LABELS[normalizedKey];
  }

  const capabilityMatch = normalizedValue.match(/^similar\s+(.+?)\s+capabilities$/i);
  if (capabilityMatch) {
    return normalizeSimilarBoardCapabilityList(capabilityMatch[1]);
  }

  const cpuMatch = normalizedValue.match(/^the same\s+(.+?)\s+cpu$/i);
  if (cpuMatch) {
    return `Same ${cpuMatch[1]} CPU`;
  }

  const logicLevelMatch = normalizedValue.match(/^the same\s+(.+?)\s+logic level$/i);
  if (logicLevelMatch) {
    return `Same ${logicLevelMatch[1]} logic`;
  }

  const sameMatch = normalizedValue.match(/^the same\s+(.+)$/i);
  if (sameMatch) {
    return `Same ${toTitleLikeText(sameMatch[1])}`;
  }

  const similarMatch = normalizedValue.match(/^a (?:very\s+)?similar\s+(.+)$/i);
  if (similarMatch) {
    return `Similar ${similarMatch[1]}`;
  }

  return toTitleLikeText(normalizedValue);
};

const extractSimilarBoardReasonParts = (reason, reasons = []) => {
  const explicitReasons = normalizeStringArray(reasons);
  if (explicitReasons.length > 0) {
    return explicitReasons;
  }

  const cleanedReason = String(reason ?? '')
    .trim()
    .replace(/^Similar because it shares\s+/i, '')
    .replace(/^Similar because it\s+/i, '')
    .replace(/\.$/, '');

  if (!cleanedReason) {
    return [];
  }

  const matches = [];

  SIMILAR_BOARD_REASON_PART_PATTERNS.forEach((pattern) => {
    pattern.lastIndex = 0;

    Array.from(cleanedReason.matchAll(pattern)).forEach((match) => {
      const matchValue = String(match[0] ?? '').trim();
      const matchIndex = Number.isFinite(match.index) ? match.index : 0;

      if (!matchValue) {
        return;
      }

      matches.push({
        value: matchValue,
        index: matchIndex,
      });
    });
  });

  if (matches.length === 0) {
    return [cleanedReason];
  }

  return matches
    .sort((left, right) => left.index - right.index)
    .map((entry) => entry.value);
};

const formatSimilarBoardReason = (reason, reasons = []) => {
  const displayParts = extractSimilarBoardReasonParts(reason, reasons)
    .map(formatSimilarBoardReasonPart)
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index);

  if (displayParts.length === 0) {
    return '';
  }

  return displayParts.slice(0, 2).join(' · ');
};

const OFFICIAL_REFERENCE_PATTERN = /\b(documentation|docs?|datasheet|schematic|manual)\b/i;
const GUIDE_REFERENCE_PATTERN = /\b(tutorial|guide|getting started)\b/i;
const REFERENCE_GROUP_LABELS = {
  official: 'Official sources',
  guides: 'Related guides',
  generic: 'References',
};

const COMMON_REFERENCE_HOST_PREFIXES = new Set([
  'www',
  'docs',
  'doc',
  'learn',
  'developer',
  'developers',
  'support',
  'wiki',
  'help',
  'blog',
  'kb',
]);

const toSentenceCase = (value) => {
  const normalizedValue = String(value ?? '').trim();
  if (!normalizedValue) {
    return '';
  }

  return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
};

const getReferenceDomain = (url) => {
  try {
    return new URL(String(url ?? '')).hostname.replace(/^www\./i, '');
  } catch (error) {
    return '';
  }
};

const getReferenceSourceName = (url) => {
  const domain = getReferenceDomain(url);
  if (!domain) {
    return '';
  }

  const parts = domain
    .split('.')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);

  const sourcePart = parts.find((part) => !COMMON_REFERENCE_HOST_PREFIXES.has(part)) || parts[0] || '';

  return sourcePart
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => toSentenceCase(part))
    .join(' ');
};

const inferReferenceTitleFromUrl = (url) => {
  try {
    const pathname = new URL(String(url ?? '')).pathname.toLowerCase();

    if (pathname.includes('getting-started')) {
      return 'Getting Started Guide';
    }

    if (pathname.includes('datasheet')) {
      return 'Datasheet';
    }

    if (pathname.includes('schematic')) {
      return 'Schematic';
    }

    if (pathname.includes('manual')) {
      return 'Manual';
    }

    if (pathname.includes('documentation') || /\/docs?(?:\/|$)/.test(pathname)) {
      return 'Documentation';
    }

    if (pathname.includes('tutorial')) {
      return 'Tutorial';
    }

    if (pathname.includes('guide')) {
      return 'Guide';
    }
  } catch (error) {
    return '';
  }

  return '';
};

const getReferenceGroup = (reference) => {
  const typeText = String(reference?.type ?? reference?.kind ?? '').trim().toLowerCase();
  const titleText = String(reference?.title ?? inferReferenceTitleFromUrl(reference?.url) ?? '').trim();
  const combinedText = `${typeText} ${titleText}`.trim();

  if (['documentation', 'docs', 'datasheet', 'schematic', 'manual', 'official'].includes(typeText)) {
    return 'official';
  }

  if (['tutorial', 'guide', 'getting-started', 'walkthrough'].includes(typeText)) {
    return 'guides';
  }

  if (OFFICIAL_REFERENCE_PATTERN.test(combinedText)) {
    return 'official';
  }

  if (GUIDE_REFERENCE_PATTERN.test(combinedText)) {
    return 'guides';
  }

  return 'generic';
};

const getReferenceAccentClass = (reference) => {
  const referenceGroup = getReferenceGroup(reference);

  if (referenceGroup === 'official') {
    return 'official';
  }

  if (referenceGroup === 'guides') {
    return 'guides';
  }

  return 'generic';
};

const getReferenceDescription = (reference) => {
  const referenceTitle = String(reference?.title ?? inferReferenceTitleFromUrl(reference?.url) ?? 'Reference').trim().toLowerCase();
  const sourceName = getReferenceSourceName(reference?.url);
  const boardName = String(reference?.boardName ?? '').trim();
  const isPdf = /\.pdf(?:$|[?#])/i.test(String(reference?.url ?? ''));
  const accentClass = getReferenceAccentClass(reference);

  if (referenceTitle.includes('datasheet')) {
    return `${boardName || 'Technical'} ${isPdf ? 'technical datasheet PDF' : 'technical datasheet'}`;
  }

  if (referenceTitle.includes('schematic')) {
    return `${sourceName ? `Official ${sourceName}` : 'Official'} schematic and hardware reference`;
  }

  if (referenceTitle.includes('manual')) {
    return `${sourceName ? `Official ${sourceName}` : 'Official'} manual and hardware reference`;
  }

  if (referenceTitle.includes('documentation') || referenceTitle === 'docs') {
    return `${sourceName ? `Official ${sourceName}` : 'Official'} documentation`;
  }

  if (referenceTitle.includes('getting started')) {
    return 'Getting started guide and setup walkthrough';
  }

  if (referenceTitle.includes('guide')) {
    return 'Related guide and practical walkthrough';
  }

  if (referenceTitle.includes('tutorial')) {
    return 'Step-by-step tutorial and example';
  }

  if (accentClass === 'official') {
    return `${sourceName ? `Official ${sourceName}` : 'Official'} reference material`;
  }

  if (accentClass === 'guides') {
    return 'Related guide, walkthrough, or project tutorial';
  }

  return 'Reference link';
};

const getReferenceIcon = (reference) => {
  const referenceText = `${reference?.type ?? ''} ${reference?.title ?? ''}`.toLowerCase();

  if (referenceText.includes('datasheet')) {
    return BookOpen;
  }

  if (/\b(documentation|docs?|manual|schematic)\b/.test(referenceText)) {
    return FileText;
  }

  if (/\b(tutorial|guide|get(?:ting)? started)\b/.test(referenceText)) {
    return Compass;
  }

  return Link2;
};

const PIN_GROUP_UI_COLORS = {
  gpio: { background: '#eff6ff', border: PIN_CAPABILITY_COLORS.gpio, text: '#1d4ed8', hover: '#dbeafe' },
  uart: { background: '#f0fdf4', border: PIN_CAPABILITY_COLORS.uart, text: '#15803d', hover: '#dcfce7' },
  i2c: { background: '#faf5ff', border: PIN_CAPABILITY_COLORS.i2c, text: '#7e22ce', hover: '#f3e8ff' },
  spi: { background: '#fff7ed', border: PIN_CAPABILITY_COLORS.spi, text: '#c2410c', hover: '#ffedd5' },
  analog: { background: '#f0fdfa', border: PIN_CAPABILITY_COLORS.analog, text: '#0f766e', hover: '#ccfbf1' },
  power: { background: '#fff1f2', border: PIN_CAPABILITY_COLORS.power, text: '#b91c1c', hover: '#ffe4e6' },
  ground: { background: '#f1f5f9', border: PIN_CAPABILITY_COLORS.ground, text: '#000000', hover: '#e2e8f0' },
  pwm: { background: '#fefce8', border: PIN_CAPABILITY_COLORS.pwm, text: '#854d0e', hover: '#fef9c3' },
  control: { background: '#fdf2f8', border: PIN_CAPABILITY_COLORS.control, text: '#be185d', hover: '#fce7f3' },
  special: { background: '#fdf2f8', border: PIN_CAPABILITY_COLORS.special, text: '#be185d', hover: '#fce7f3' },
};

const resolvePinGroupVisualKey = (value) => {
  const normalizedValue = String(value ?? '').trim().toLowerCase();

  if (!normalizedValue) {
    return 'special';
  }

  return normalizePinFilterGroup(normalizedValue) || 'special';
};

const getPinGroupUiColors = (value) => PIN_GROUP_UI_COLORS[resolvePinGroupVisualKey(value)] || PIN_GROUP_UI_COLORS.special;

const contentCardStyles = css`
  background: linear-gradient(180deg, #ffffff 0%, #fcfdff 100%);
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
`;

const sidebarCardStyles = css`
  background: linear-gradient(180deg, #ffffff 0%, #fcfdff 100%);
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
`;

const BoardDetailContainer = styled.div`
  padding: 8px 0 clamp(24px, 3vw, 36px);
  background: linear-gradient(180deg, #f8fafc 0px, #ffffff 240px);
`;

const BoardDetailInner = styled.div`
  width: min(${BOARD_DETAIL_LAYOUT_MAX_WIDTH}, calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
`;

const BoardHeader = styled.div`
  display: grid;
  gap: 24px;
  margin-bottom: 24px;
  margin-top: 20px;
`;

const PreviewStateRow = styled.div`
  display: flex;
  justify-content: center;
`;

const PreviewStateChip = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #a5f3fc;
  background: #ecfeff;
  color: #155e75;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-align: center;
`;

const BoardSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(320px, 2fr);
  gap: clamp(24px, 3vw, 40px);
  align-items: start;
  width: 100%;

  @media (max-width: 1280px) {
    grid-template-columns: minmax(0, 1.45fr) minmax(300px, 360px);
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const ActionButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const actionButtonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  color: white;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    transform: translateY(-1px);
  }
`;

const EditButton = styled(Link)`
  ${actionButtonStyles}
  background: #3b82f6;
  text-decoration: none;

  &:hover {
    background: #2563eb;
    color: white;
  }
`;

const CompareButton = styled.button`
  ${actionButtonStyles}
  background: #10b981;
  
  &:hover {
    background: #059669;
  }
`;

const ExportButton = styled.button`
  ${actionButtonStyles}
  background: #0f766e;

  &:hover {
    background: #115e59;
  }
`;

const ShareButton = styled.button`
  ${actionButtonStyles}
  background: #7c3aed;

  &:hover {
    background: #6d28d9;
  }
`;

const BoardTitle = styled.h1`
  font-size: clamp(1.25rem, 3vw, 2.5rem);
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  text-align: center;
`;

const VerificationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  justify-self: start;
  width: fit-content;
  padding: 6px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  border: 1px solid
    ${props => {
      if (props.$status === 'verified') {
        return '#bbf7d0';
      }

      if (props.$status === 'reviewed') {
        return '#fde68a';
      }

      return '#dbe4f0';
    }};
  color:
    ${props => {
      if (props.$status === 'verified') {
        return '#166534';
      }

      if (props.$status === 'reviewed') {
        return '#92400e';
      }

      return '#475569';
    }};
  background:
    ${props => {
      if (props.$status === 'verified') {
        return '#f0fdf4';
      }

      if (props.$status === 'reviewed') {
        return '#fffbeb';
      }

      return '#f8fafc';
    }};
`;

const SidebarDetailList = styled.div`
  display: grid;
  gap: 18px;
`;

const SidebarDetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const SidebarDetailTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const SidebarDetailGroup = styled.div`
  display: grid;
  gap: 10px;
`;

const SidebarDetailGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
`;

const SidebarDetailGroupCards = styled.div`
  display: grid;
  gap: 10px;
`;

const SidebarDetailCard = styled.a`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 12px;
  min-width: 0;
  padding: 14px 14px 14px 12px;
  border-radius: 14px;
  border: 1px solid #dbe4ea;
  border-left: 4px solid
    ${props => {
      if (props.$accent === 'official') {
        return '#60a5fa';
      }

      if (props.$accent === 'guides') {
        return '#34d399';
      }

      return '#94a3b8';
    }};
  background: #ffffff;
  text-decoration: none;
  box-shadow: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08);
    border-color: #cbd5e1;
  }

  &:focus-visible {
    outline: none;
    transform: translateY(-1px);
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.14);
  }
`;

const SidebarDetailCardIconBlock = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background:
    ${props => {
      if (props.$accent === 'official') {
        return '#dbeafe';
      }

      if (props.$accent === 'guides') {
        return '#dcfce7';
      }

      return '#e2e8f0';
    }};
  color:
    ${props => {
      if (props.$accent === 'official') {
        return '#1d4ed8';
      }

      if (props.$accent === 'guides') {
        return '#047857';
      }

      return '#475569';
    }};
`;

const SidebarDetailCardContent = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
`;

const SidebarDetailCardLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  color: #0f172a;
  overflow-wrap: anywhere;
`;

const SidebarDetailCardDescription = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: #475569;
  overflow-wrap: anywhere;
`;

const SidebarDetailCardUrl = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #64748b;
  overflow-wrap: anywhere;
`;

const SidebarDetailCardArrow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  flex-shrink: 0;
  margin-top: 2px;
`;

const SidebarDetailEmptyState = styled.div`
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px dashed #d7dee8;
  background: rgba(255, 255, 255, 0.82);
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
`;

const BoardSubtitle = styled.div`
  ${richContentBodyStyles}
  font-size: 18px;
  color: #64748b;
  line-height: 1.6;
  text-align: left;
  width: 100%;
  min-width: 0;
`;

const ProtocolBadgeList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
  align-items: center;
  align-content: flex-start;
`;

const ProtocolCheckBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid #bbf7d0;
  background: #f0fdf4;
  color: #166534;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.15;

  svg {
    flex-shrink: 0;
  }
`;

const DetailsEmptyText = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 15px;
  line-height: 1.6;
`;

const BoardSpecsColumn = styled.div`
  display: grid;
  gap: 18px;
  align-content: start;
  grid-column: 1 / -1;
`;

const BoardSpecs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const SpecItem = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  text-align: center;
  min-width: 118px;
  flex: 0 0 auto;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.82);
`;

const SpecLabel = styled.div`
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
`;

const SpecValue = styled.div`
  font-size: clamp(1.05rem, 1vw, 1.18rem);
  color: #1e293b;
  font-weight: 700;
  line-height: 1.3;
`;

const MainContentColumn = styled.div`
  display: grid;
  gap: clamp(18px, 2vw, 24px);
  align-content: start;
  min-width: 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$hasSidebar ? 'minmax(0, 1fr) minmax(292px, 332px)' : '1fr'};
  gap: clamp(28px, 3vw, 44px);
  align-items: start;

  @media (max-width: 1280px) {
    grid-template-columns: ${props => props.$hasSidebar ? 'minmax(0, 1fr) minmax(270px, 310px)' : '1fr'};
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const PinoutSection = styled.div`
  position: relative;
  display: grid;
  justify-items: center;
  gap: 16px;
  padding: clamp(18px, 2vw, 24px);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 22px;
  border: 1px solid #dbe7f3;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  width: 100%;
  min-width: 0;
  scroll-margin-top: 80px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at top, rgba(59, 130, 246, 0.08), transparent 56%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const StaticPinoutDetails = styled.details`
  ${contentCardStyles}
  overflow: hidden;

  &[open] summary::after {
    transform: rotate(180deg);
  }
`;

const StaticPinoutSummary = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: clamp(18px, 2vw, 24px);
  color: #1e293b;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &::after {
    content: '\\2304';
    color: #64748b;
    font-size: 22px;
    line-height: 1;
    transition: transform 0.2s ease;
  }

  &:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: -4px;
  }
`;

const StaticPinoutImageWrap = styled.div`
  padding: 0 clamp(18px, 2vw, 24px) clamp(18px, 2vw, 24px);
`;

const StaticPinoutImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
`;

const SvgLoadingState = styled.div`
  width: 100%;
  max-width: ${BOARD_DETAIL_PREVIEW_MAX_WIDTH};
  min-height: clamp(320px, 52vh, 760px);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  color: #64748b;
  font-size: 15px;
  text-align: center;
  padding: 24px;
`;

const PinoutTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterSection = styled.div`
  display: grid;
  gap: 16px;
`;

const FilterTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
`;

const FilterButton = styled.button`
  ${props => {
    const colors = getPinGroupUiColors(props.$groupValue);

    return css`
      --filter-chip-border: ${props.$active ? colors.border : '#dbe4f0'};
      --filter-chip-background: ${props.$active ? colors.background : '#ffffff'};
      --filter-chip-color: ${props.$active ? colors.text : '#475569'};
      --filter-chip-hover: ${props.$active ? colors.hover : '#f8fafc'};
    `;
  }}
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 9px;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--filter-chip-border);
  background: var(--filter-chip-background);
  color: var(--filter-chip-color);
  border-radius: 12px;
  font-size: 13px;
  font-weight: ${props => props.$active ? 700 : 600};
  text-align: left;
  cursor: pointer;
  box-shadow: ${props => props.$active ? '0 1px 2px rgba(15, 23, 42, 0.06), inset 0 0 0 1px rgba(255, 255, 255, 0.72)' : 'none'};
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &::before {
    content: '';
    width: 9px;
    height: 9px;
    flex: 0 0 9px;
    border-radius: 999px;
    background: ${props => getPinGroupUiColors(props.$groupValue).border};
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
  }
  
  &:hover {
    border-color: ${props => getPinGroupUiColors(props.$groupValue).border};
    background: var(--filter-chip-hover);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: 2px;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const GallerySection = styled.div`
  ${contentCardStyles}
  padding: clamp(20px, 2vw, 32px);
  min-width: 0;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;

  & > * {
    width: 100%;
    max-width: 400px;
    justify-self: center;
  }
`;

const GalleryLink = styled.a`
  display: block;
  width: 100%;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
`;

const GalleryCaption = styled.div`
  padding: 12px 14px;
  font-size: 14px;
  color: #475569;
  background: white;
`;

const InfoPanel = styled.div`
  ${sidebarCardStyles}
  padding: ${props => props.$compact ? '18px 18px 16px' : '22px'};
  scroll-margin-top: 80px;
`;

const ReferenceInfoPanel = styled(InfoPanel)`
  padding: 22px;
  background: linear-gradient(180deg, #fcfdff 0%, #f7fbff 100%);
  border: 1px solid #dce7f3;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
`;

const PanelTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: ${props => props.$compact ? '10px' : '16px'};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PinInfo = styled.div`
  text-align: center;
  color: #64748b;
  font-style: italic;
  font-size: 14px;
  line-height: 1.5;
  max-width: 28ch;
  margin: 0 auto;
`;

const AffiliateLinksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AffiliateLinkCard = styled.a`
  display: block;
  padding: 14px 16px;
  ${sidebarCardStyles}
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
    border-color: #cbd5e1;
  }
`;

const AffiliateLinkLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 4px;
`;

const AffiliateLinkUrl = styled.div`
  font-size: 13px;
  color: #64748b;
  word-break: break-all;
`;

const SponsoredAdCard = styled.div`
  ${sidebarCardStyles}
  padding: 16px;
  display: grid;
  gap: 12px;
`;

const SponsoredAdHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const SponsoredAdLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const SponsoredAdLink = styled.a`
  display: block;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const SponsoredAdImage = styled.img`
  width: 100%;
  display: block;
  border-radius: 12px;
  border: 1px solid #dbe3ef;
  background: #ffffff;
`;

const SimilarBoardsList = styled.div`
  display: grid;
  gap: 12px;
`;

const SimilarBoardCard = styled(Link)`
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
    border-color: #cbd5e1;
  }
`;

const SimilarBoardCardIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  border: 1px solid #dbe4f0;
  background:
    radial-gradient(circle at top, rgba(44, 159, 164, 0.12), transparent 60%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
`;

const SimilarBoardCardIconImage = styled.img`
  width: 100%;
  height: 100%;
  padding: 6px;
  display: block;
  object-fit: contain;
`;

const SimilarBoardCardIconFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2c9fa4;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const SimilarBoardCardContent = styled.div`
  min-width: 0;
  display: grid;
  gap: 4px;
  align-content: start;
`;

const SimilarBoardName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SimilarBoardMeta = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`;

const SimilarBoardReason = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.35;
  color: #475569;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PinDetails = styled.div`
  .pin-name {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
  }
  
  .pin-number {
    font-size: 14px;
    color: #64748b;
    margin-bottom: 16px;
  }
  
  .pin-functions {
    background: #f1f5f9;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    color: #475569;
  }
  
  .pin-specs {
    display: grid;
    gap: 8px;
  }
  
  .spec-row {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    align-items: start;
    gap: 12px;
    font-size: 14px;
  }
  
  .spec-label {
    color: #64748b;
  }
  
  .spec-value {
    color: #1e293b;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 4px;
    min-width: 0;
    text-align: right;
  }
  
  .pin-description {
    background: #f8fafc;
    padding: 12px;
    border-radius: 8px;
    margin-top: 16px;
    font-size: 14px;
    color: #475569;
    border-left: 3px solid #2c9fa4;
    line-height: 1.5;
    
    /* Preserve rich text formatting */
    h1, h2, h3, h4, h5, h6 {
      margin: 12px 0 6px 0;
      font-weight: 600;
      color: #1e293b;
      font-size: 16px;
    }
    
    p {
      margin: 6px 0;
    }
    
    ul, ol {
      margin: 6px 0;
      padding-left: 20px;
    }
    
    li {
      margin: 2px 0;
    }
    
    blockquote {
      border-left: 3px solid #2c9fa4;
      padding-left: 12px;
      margin: 12px 0;
      font-style: italic;
      color: #64748b;
    }
    
    code {
      background: #e2e8f0;
      padding: 1px 4px;
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      font-size: 12px;
    }
    
    pre {
      background: #e2e8f0;
      padding: 8px;
      border-radius: 6px;
      overflow-x: auto;
      margin: 12px 0;
    }
    
    pre code {
      background: none;
      padding: 0;
    }
    
    a {
      color: #2c9fa4;
      text-decoration: underline;
    }
    
    a:hover {
      color: #2563eb;
    }
    
    strong {
      font-weight: 600;
      color: #1e293b;
    }
    
    em {
      font-style: italic;
    }
    
    img {
      max-width: 100%;
      height: auto;
      border-radius: 6px;
      margin: 12px 0;
    }
  }
`;

const PinGroupBadge = styled.span`
  ${props => {
    const colors = getPinGroupUiColors(props.$groupValue);

    return css`
      background: ${colors.background};
      border: 1px solid ${colors.border};
      color: ${colors.text};
    `;
  }}
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.2;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #ef4444;
  font-size: 18px;
  margin: 40px 0;
`;

const EmbedButton = styled.button`
  ${actionButtonStyles}
  background: #f59e0b;

  &:hover {
    background: #d97706;
  }
`;

const EmbedModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const EmbedModalContent = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const EmbedModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const EmbedModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
`;

const EmbedModalClose = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  
  &:hover {
    color: #374151;
  }
`;

const EmbedCodeContainer = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  position: relative;
`;

const EmbedCode = styled.pre`
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  color: #374151;
  white-space: pre-wrap;
  word-break: break-all;
`;

const CopyButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &.copied {
    background: #10b981;
    color: white;
    border-color: #10b981;
  }
`;

const EmbedPreview = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 16px;
`;

const DetailsSection = styled.div`
  ${contentCardStyles}
  padding: clamp(20px, 2vw, 32px);
  margin-top: 0;
  width: 100%;
  min-width: 0;
  grid-column: 1 / 2;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-column: 1;
  }
`;

const PinTableSection = styled(DetailsSection)`
  margin-top: 0;
`;

const SupportedProtocolsSection = styled(PinTableSection)`
  padding-top: clamp(18px, 2vw, 24px);
  padding-bottom: clamp(18px, 2vw, 24px);
  display: grid;
  gap: 16px;

  ${PinoutTitle} {
    margin-bottom: 0;
  }
`;

const QuickGuideSection = styled(PinTableSection)`
  border-left: 4px solid #60a5fa;
  padding-left: calc(clamp(20px, 2vw, 32px) - 4px);
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
`;

const MobileScrollHint = styled.div`
  display: none;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.01em;

  @media (max-width: 900px) {
    display: block;
  }
`;

const PinTableScroll = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }
`;

const SummaryTableScroll = styled(PinTableScroll)`
  @media (max-width: 768px) {
    overflow: visible;
    border: none;
    background: transparent;
  }
`;

const PinTable = styled.table`
  width: 100%;
  min-width: 780px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;

  th,
  td {
    padding: 12px 16px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #f8fafc;
    color: #334155;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  thead th:first-child {
    border-top-left-radius: 14px;
  }

  thead th:last-child {
    border-top-right-radius: 14px;
  }

  td {
    color: #475569;
    line-height: 1.5;
  }

  tbody tr:nth-child(even) {
    background: #f8fafc;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr[data-interactive='true'] {
    cursor: pointer;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  tbody tr[data-interactive='true']:hover {
    background: #eff6ff;
  }

  tbody tr[data-interactive='true']:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: -2px;
    background: #eff6ff;
  }

  tbody tr[data-selected='true'] {
    background: #dbeafe;
    box-shadow: inset 4px 0 0 #2563eb;
  }

  tbody tr[data-selected='true']:hover,
  tbody tr[data-selected='true']:focus-visible {
    background: #dbeafe;
  }

  tbody tr[data-selected='true'] td {
    color: #1e3a8a;
  }

  strong {
    color: #1e293b;
    font-weight: 600;
  }
`;

const DetailedPinTable = styled(PinTable)`
  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    box-shadow: inset 0 -1px 0 #dbe3ef;
  }
`;

const QuickPinTable = styled(PinTable)`
  min-width: 420px;

  tbody tr[data-selected='true'],
  tbody tr[data-selected='true']:hover,
  tbody tr[data-selected='true']:focus-visible {
    background: #dbeafe;
  }

  tbody tr[data-selected='true'] td {
    color: #1e3a8a;
  }

  @media (max-width: 768px) {
    min-width: 0;
    border-collapse: separate;
    border-spacing: 0;

    &,
    thead,
    tbody,
    tr,
    th,
    td {
      display: block;
      width: 100%;
    }

    thead {
      display: none;
    }

    tbody {
      display: grid;
      gap: 12px;
    }

    tbody tr {
      margin: 0;
      padding: 12px 14px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: #ffffff;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    }

    tbody tr:nth-child(even) {
      background: #ffffff;
    }

    tbody tr[data-interactive='true'] {
      cursor: pointer;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    tbody tr[data-selected='true'],
    tbody tr[data-selected='true']:nth-child(even) {
      background: #dbeafe;
      border-color: #93c5fd;
      box-shadow: inset 4px 0 0 #2563eb, 0 1px 2px rgba(15, 23, 42, 0.04);
    }

    td {
      display: grid;
      grid-template-columns: minmax(110px, 38%) minmax(0, 1fr);
      align-items: start;
      gap: 12px;
      padding: 6px 0;
      border: none;
      white-space: normal;
      word-break: break-word;
    }

    td::before {
      content: attr(data-label);
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.02em;
      color: #64748b;
      text-transform: uppercase;
    }

    tbody tr[data-interactive='true']:focus-visible {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
    }
  }
`;

const BackToDiagramButton = styled.button`
  display: none;
  width: 100%;
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #334155;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }

  &:hover {
    background: #f1f5f9;
  }
`;

const Copyright = styled.div`
  width: min(${BOARD_DETAIL_LAYOUT_MAX_WIDTH}, calc(100% - (var(--site-gutter) * 2)));
  text-align: center;
  margin: 40px auto 0;
  padding-top: 22px;
  border-top: 1px solid #e5e7eb;
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.7;
`;

const formatPublicBoardTitle = (boardName) => {
  const normalized = String(boardName || '').trim();

  if (!normalized) {
    return '';
  }

  return /pinout$/i.test(normalized) ? normalized : `${normalized} Pinout`;
};

const buildBoardPinoutImageAlt = (boardName) => {
  const normalizedTitle = String(boardName || '')
    .trim()
    .replace(/\s+pinout$/i, '');

  return normalizedTitle
    ? `${normalizedTitle} pinout diagram`
    : 'Board pinout diagram';
};

const getPlainTableText = (value) => stripHtmlToText(value).replace(/\s*,\s*/g, ', ').trim();

const QUICK_PIN_RANGE_DASH = '\u2013';
const QUICK_PIN_ROW_ORDER = ['gpio', 'pwm', 'analog', 'i2c', 'spi', 'uart', 'power'];
const QUICK_PIN_ROW_LABELS = {
  gpio: 'General GPIO',
  pwm: 'PWM',
  analog: 'Analog input',
  i2c: 'I2C',
  spi: 'SPI',
  uart: 'UART',
  power: 'Power',
};

const QUICK_PIN_ANNOTATION_ORDER = {
  SDA: 0,
  SCL: 1,
  RX: 0,
  TX: 1,
  CS: 0,
  SS: 0,
  MOSI: 1,
  MISO: 2,
  SCK: 3,
};

const QUICK_PIN_POWER_ORDER = {
  VIN: 0,
  RAW: 1,
  '5V': 2,
  VCC: 3,
  '3V3': 4,
  GND: 5,
};

const normalizeQuickPinSpacing = (value) => String(value || '')
  .replace(/\s*\/\s*/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeQuickPinPowerLabel = (value) => {
  const upperValue = String(value || '').trim().toUpperCase();

  if (upperValue === '3.3V') {
    return '3V3';
  }

  if (upperValue === 'GROUND') {
    return 'GND';
  }

  return upperValue;
};

const normalizeQuickPinPowerListLabel = (value) => {
  const compactValue = String(value || '').trim().replace(/\s+/g, '').toUpperCase();

  if (/^(?:3\.3V|3V3)\d+$/.test(compactValue)) {
    return '3V3';
  }

  if (/^GROUND\d+$/.test(compactValue)) {
    return 'GND';
  }

  const numberedPowerMatch = compactValue.match(/^(VIN|RAW|5V|VCC|VDD|GND)\d+$/);
  if (numberedPowerMatch) {
    return numberedPowerMatch[1];
  }

  return normalizeQuickPinPowerLabel(value);
};

const extractQuickPinPrimaryIdentifier = (value) => {
  const normalizedValue = normalizeQuickPinSpacing(value);
  if (!normalizedValue) {
    return '';
  }

  const normalizedPowerLabel = normalizeQuickPinPowerLabel(normalizedValue);
  if (QUICK_PIN_POWER_ORDER[normalizedPowerLabel] !== undefined) {
    return normalizedPowerLabel;
  }

  const matchers = [
    /^((?:GPIO|ADC|DAC|IO|D|A)\d+)\b/i,
    /^(P\d+(?:\.\d+)?)\b/i,
    /^([A-Z]+\d+)\b/i,
  ];

  for (const matcher of matchers) {
    const match = normalizedValue.match(matcher);
    if (match) {
      return match[1].toUpperCase();
    }
  }

  return normalizedValue;
};

const buildQuickPinSearchText = (pin) => normalizeQuickPinSpacing([
  pin?.pin_name,
  pin?.functions,
  pin?.alternate_functions,
  pin?.alternate_pin_names,
  pin?.pin_role,
  pin?.pinRole,
  ...(getPinCapabilityTags(pin))
].filter(Boolean).join(' ')).toLowerCase();

const buildQuickPinMetadata = (pin) => {
  const rawLabel = getPlainTableText(pin?.pin_name) || getPlainTableText(pin?.pin_number);
  const displayLabel = extractQuickPinPrimaryIdentifier(rawLabel);
  const pinRole = String(pin?.pin_role ?? pin?.pinRole ?? '').trim().toLowerCase();
  const capabilityTags = getPinCapabilityTags(pin);
  const searchText = buildQuickPinSearchText(pin);

  const hasRole = (...roles) => roles.includes(pinRole);
  const hasTag = (...tags) => capabilityTags.some((tag) => tags.includes(tag));
  const hasTagPrefix = (...prefixes) => capabilityTags.some((tag) => (
    prefixes.some((prefix) => tag === prefix || tag.startsWith(`${prefix}-`))
  ));
  const matches = (pattern) => pattern.test(searchText);
  const looksLikeDigitalPin = /^(?:D|GPIO|IO)\d+$/i.test(displayLabel);
  const looksLikeAnalogPin = /^A\d+$/i.test(displayLabel);
  const isPower = (
    hasRole('ground', 'power-input', 'power-output-3v3', 'power-output-5v')
    || hasTag('ground', 'gnd', 'power', 'power-input', 'power-output', 'power-3v3', 'power-5v', 'power-vin')
    || /^(?:VIN|RAW|5V|VCC|3V3|3\.3V|GND|GROUND)$/i.test(displayLabel)
  );
  const isAnalog = (
    displayLabel !== 'AREF'
    && (
      hasRole('analog-in', 'analog-input')
      || hasTag('analog', 'analog-in', 'analog-input', 'adc')
      || hasTagPrefix('analog')
      || looksLikeAnalogPin
      || matches(/\banalog(?:\s+input)?\b|\badc\b/)
    )
  );
  const isI2cSda = (
    hasRole('i2c-sda')
    || hasTag('i2c-sda')
    || matches(/\bi2c\s+sda\b|\bsda\b/)
  );
  const isI2cScl = (
    hasRole('i2c-scl')
    || hasTag('i2c-scl')
    || matches(/\bi2c\s+scl\b|\bscl\b/)
  );
  const isSpiMosi = (
    hasRole('spi-mosi')
    || hasTag('spi-mosi')
    || matches(/\bspi\b.*\bmosi\b|\bmosi\b/)
  );
  const isSpiMiso = (
    hasRole('spi-miso')
    || hasTag('spi-miso')
    || matches(/\bspi\b.*\bmiso\b|\bmiso\b/)
  );
  const isSpiSck = (
    hasRole('spi-sck')
    || hasTag('spi-sck')
    || matches(/\bspi\b.*\bsck\b|\bsck\b/)
  );
  const isSpiCs = (
    hasRole('spi-cs')
    || hasTag('spi-cs', 'chip-select')
    || matches(/\bchip\s+select\b|\bspi\b.*\bcs\b|\bss\b/)
  );
  const isUartRx = (
    hasRole('uart-rx')
    || hasTag('uart-rx')
    || matches(/\buart\s+rx\b|\brx\b/)
  );
  const isUartTx = (
    hasRole('uart-tx')
    || hasTag('uart-tx')
    || matches(/\buart\s+tx\b|\btx\b/)
  );
  const isPwm = (
    hasRole('pwm')
    || hasTag('pwm')
    || hasTagPrefix('pwm')
    || matches(/\bpwm\b/)
  );
  const isGpio = (
    hasRole('gpio')
    || hasTag('gpio', 'digital', 'digital-in', 'digital-input', 'digital-out', 'digital-output')
    || looksLikeDigitalPin
    || matches(/\bgpio\b|\bdigital(?:\s+i\/o)?\b/)
  );

  return {
    displayLabel,
    isPower,
    isAnalog,
    isI2cSda,
    isI2cScl,
    isSpiMosi,
    isSpiMiso,
    isSpiSck,
    isSpiCs,
    isUartRx,
    isUartTx,
    isPwm,
    isGpio,
  };
};

const getPinCalloutBaseColor = (pin) => {
  return getDefaultPublicPinCalloutColor(pin);
};

const compareQuickPinLabels = (leftValue, rightValue) => (
  String(leftValue || '').localeCompare(String(rightValue || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
);

const isPreferredQuickPinLabel = (label) => (
  /^(?:GPIO|ADC|DAC|IO|D|A)\d+$/i.test(String(label || ''))
  || /^P\d+(?:\.\d+)?$/i.test(String(label || ''))
  || QUICK_PIN_POWER_ORDER[String(label || '').toUpperCase()] !== undefined
);

const compressQuickPinLabelRange = (labels) => {
  if (!Array.isArray(labels) || labels.length < 2) {
    return '';
  }

  const parsedLabels = labels.map((label) => {
    const match = String(label).match(/^([A-Za-z]+)(\d+)$/);
    if (!match) {
      return null;
    }

    return {
      prefix: match[1].toUpperCase(),
      numericValue: Number(match[2]),
    };
  });

  if (parsedLabels.some((entry) => !entry)) {
    return '';
  }

  const [{ prefix: firstPrefix, numericValue: firstNumericValue }] = parsedLabels;
  const lastEntry = parsedLabels[parsedLabels.length - 1];
  const samePrefix = parsedLabels.every((entry) => entry.prefix === firstPrefix);
  const consecutive = parsedLabels.every((entry, index) => entry.numericValue === firstNumericValue + index);

  if (!samePrefix || !consecutive) {
    return '';
  }

  return `${firstPrefix}${firstNumericValue}${QUICK_PIN_RANGE_DASH}${lastEntry.prefix}${lastEntry.numericValue}`;
};

const formatQuickPinGuideRowPins = (rowKey, items) => {
  const uniqueItems = [];
  const seenKeys = new Set();

  items.forEach((item) => {
    const dedupeKey = `${item.label}::${item.annotation || ''}`;
    if (seenKeys.has(dedupeKey)) {
      return;
    }

    seenKeys.add(dedupeKey);
    uniqueItems.push(item);
  });

  if (uniqueItems.length === 0) {
    return '';
  }

  const preferredItems = uniqueItems.filter((item) => isPreferredQuickPinLabel(item.label));
  const displayItems = (
    rowKey === 'power'
      ? uniqueItems
      : preferredItems.length > 0
        ? preferredItems
        : uniqueItems
  );

  if (rowKey === 'i2c') {
    return displayItems
      .sort((leftItem, rightItem) => {
        const leftPriority = QUICK_PIN_ANNOTATION_ORDER[leftItem.annotation] ?? 99;
        const rightPriority = QUICK_PIN_ANNOTATION_ORDER[rightItem.annotation] ?? 99;

        if (leftPriority !== rightPriority) {
          return leftPriority - rightPriority;
        }

        return compareQuickPinLabels(leftItem.label, rightItem.label);
      })
      .map((item) => `${item.label} ${item.annotation}`)
      .join(', ');
  }

  if (rowKey === 'uart') {
    return Array.from(new Set(
      displayItems
        .sort((leftItem, rightItem) => {
          const leftPriority = QUICK_PIN_ANNOTATION_ORDER[leftItem.annotation] ?? 99;
          const rightPriority = QUICK_PIN_ANNOTATION_ORDER[rightItem.annotation] ?? 99;

          if (leftPriority !== rightPriority) {
            return leftPriority - rightPriority;
          }

          return compareQuickPinLabels(leftItem.label, rightItem.label);
        })
        .map((item) => item.label)
    )).join(', ');
  }

  if (rowKey === 'power') {
    return Array.from(new Set(
      displayItems
        .map((item) => normalizeQuickPinPowerListLabel(item.label))
        .filter(Boolean)
    ))
      .sort((leftLabel, rightLabel) => {
        const leftPriority = QUICK_PIN_POWER_ORDER[leftLabel] ?? 99;
        const rightPriority = QUICK_PIN_POWER_ORDER[rightLabel] ?? 99;

        if (leftPriority !== rightPriority) {
          return leftPriority - rightPriority;
        }

        return compareQuickPinLabels(leftLabel, rightLabel);
      })
      .join(', ');
  }

  const sortedLabels = displayItems
    .map((item) => item.label)
    .sort(compareQuickPinLabels);

  if (rowKey === 'analog' || rowKey === 'spi') {
    const compressedRange = compressQuickPinLabelRange(sortedLabels);
    if (compressedRange) {
      return compressedRange;
    }
  }

  return sortedLabels.join(', ');
};

const buildQuickPinGuideRows = (pins) => {
  const bucketMap = {
    gpio: [],
    pwm: [],
    analog: [],
    i2c: [],
    spi: [],
    uart: [],
    power: [],
  };

  (Array.isArray(pins) ? pins : []).forEach((pin) => {
    const metadata = buildQuickPinMetadata(pin);
    if (!metadata.displayLabel) {
      return;
    }

    if (metadata.isPower) {
      bucketMap.power.push({ label: metadata.displayLabel, pin });
    }

    if (metadata.isAnalog) {
      bucketMap.analog.push({ label: metadata.displayLabel, pin });
    }

    if (metadata.isI2cSda) {
      bucketMap.i2c.push({ label: metadata.displayLabel, annotation: 'SDA', pin });
    }

    if (metadata.isI2cScl) {
      bucketMap.i2c.push({ label: metadata.displayLabel, annotation: 'SCL', pin });
    }

    if (metadata.isSpiCs) {
      bucketMap.spi.push({ label: metadata.displayLabel, annotation: 'CS', pin });
    }

    if (metadata.isSpiMosi) {
      bucketMap.spi.push({ label: metadata.displayLabel, annotation: 'MOSI', pin });
    }

    if (metadata.isSpiMiso) {
      bucketMap.spi.push({ label: metadata.displayLabel, annotation: 'MISO', pin });
    }

    if (metadata.isSpiSck) {
      bucketMap.spi.push({ label: metadata.displayLabel, annotation: 'SCK', pin });
    }

    if (metadata.isUartRx) {
      bucketMap.uart.push({ label: metadata.displayLabel, annotation: 'RX', pin });
    }

    if (metadata.isUartTx) {
      bucketMap.uart.push({ label: metadata.displayLabel, annotation: 'TX', pin });
    }

    if (metadata.isPwm) {
      bucketMap.pwm.push({ label: metadata.displayLabel, pin });
    }

    if (
      metadata.isGpio
      && !metadata.isPower
      && !metadata.isAnalog
      && !metadata.isI2cSda
      && !metadata.isI2cScl
      && !metadata.isSpiCs
      && !metadata.isSpiMosi
      && !metadata.isSpiMiso
      && !metadata.isSpiSck
      && !metadata.isUartRx
      && !metadata.isUartTx
    ) {
      bucketMap.gpio.push({ label: metadata.displayLabel, pin });
    }
  });

  return QUICK_PIN_ROW_ORDER
    .map((rowKey) => {
      const rowItems = bucketMap[rowKey];
      const formattedPins = formatQuickPinGuideRowPins(rowKey, rowItems);

      if (!formattedPins) {
        return null;
      }

      return {
        key: rowKey,
        useCase: QUICK_PIN_ROW_LABELS[rowKey],
        pins: formattedPins,
        relatedPins: dedupePinsBySelectionIdentity(rowItems.map((item) => item.pin)),
      };
    })
    .filter(Boolean);
};

const buildCautionPinTableRows = (pins) => (
  [...(Array.isArray(pins) ? pins : [])]
    .filter((pin) => Boolean(pin?.use_with_caution))
    .sort((leftPin, rightPin) => {
      const leftPinNumber = Number(leftPin?.pin_number);
      const rightPinNumber = Number(rightPin?.pin_number);
      const leftHasNumber = Number.isFinite(leftPinNumber);
      const rightHasNumber = Number.isFinite(rightPinNumber);

      if (leftHasNumber && rightHasNumber && leftPinNumber !== rightPinNumber) {
        return leftPinNumber - rightPinNumber;
      }

      if (leftHasNumber && !rightHasNumber) {
        return -1;
      }

      if (!leftHasNumber && rightHasNumber) {
        return 1;
      }

      return getBoardPinTableLabel(leftPin).localeCompare(getBoardPinTableLabel(rightPin));
    })
    .map((pin, index) => ({
      key: pin?.id ?? `caution-${pin?.pin_number || pin?.pin_name || index}`,
      pin,
      selectionKey: getPinSelectionIdentityKey(pin),
      boardPin: getBoardPinTableLabel(pin),
      reason: getPlainTableText(pin?.caution_reason) || '-',
    }))
);

const getBoardPinTableLabel = (pin) => {
  const pinName = getPlainTableText(pin?.pin_name);
  if (pinName) {
    return pinName;
  }

  const pinNumber = getPlainTableText(pin?.pin_number);
  return pinNumber ? `Pin ${pinNumber}` : '-';
};

const isCustomCalloutSelectionItem = (pin) => {
  const calloutType = String(pin?.callout_type ?? pin?.calloutType ?? '').trim().toLowerCase();
  if (calloutType === 'custom') {
    return true;
  }

  return Boolean(String(pin?.callout_key ?? pin?.calloutKey ?? pin?.custom_id ?? pin?.customId ?? '').trim());
};

const createCustomCalloutSelectionItem = (callout) => ({
  id: String(callout?.custom_id ?? callout?.customId ?? getCalloutLayoutKey(callout) ?? '').trim() || getCalloutLayoutKey(callout),
  callout_type: 'custom',
  callout_key: getCalloutLayoutKey(callout),
  custom_id: String(callout?.custom_id ?? callout?.customId ?? '').trim(),
  pin_name: String(callout?.label ?? callout?.pin_name ?? callout?.pinName ?? 'Custom Label').trim() || 'Custom Label',
  pin_number: '',
  group_name: String(callout?.group_name ?? callout?.groupName ?? '').trim(),
  functions: String(callout?.functions ?? '').trim(),
  alternate_functions: String(callout?.alternate_functions ?? callout?.alternateFunctions ?? '').trim(),
  description: String(callout?.description ?? '').trim(),
  pin_role: String(callout?.pin_role ?? callout?.pinRole ?? '').trim(),
  capability_tags: normalizeStringArray(callout?.capability_tags ?? callout?.capabilityTags ?? []),
  voltage_min_v: callout?.voltage_min_v ?? callout?.voltageMinV ?? null,
  voltage_max_v: callout?.voltage_max_v ?? callout?.voltageMaxV ?? null,
  current_limit_ma: callout?.current_limit_ma ?? callout?.currentLimitMa ?? null,
  is_5v_tolerant: Boolean(callout?.is_5v_tolerant ?? callout?.is5vTolerant),
  use_with_caution: Boolean(callout?.use_with_caution ?? callout?.useWithCaution),
  caution_reason: String(callout?.caution_reason ?? callout?.cautionReason ?? '').trim(),
});

const getPinSelectionIdentityKey = (pin) => {
  if (isCustomCalloutSelectionItem(pin)) {
    return [
      'custom',
      String(pin?.callout_key ?? pin?.calloutKey ?? pin?.custom_id ?? pin?.customId ?? '').trim(),
      String(pin?.pin_name ?? '').trim(),
    ].join('::');
  }

  return [
    String(pin?.svgId ?? pin?.svg_id ?? '').trim(),
    String(pin?.pin_number ?? '').trim(),
    String(pin?.pin_name ?? '').trim(),
  ].join('::');
};

const dedupePinsBySelectionIdentity = (pins) => {
  const uniquePins = [];
  const seenKeys = new Set();

  (Array.isArray(pins) ? pins : []).forEach((pin) => {
    if (!pin) {
      return;
    }

    const selectionKey = getPinSelectionIdentityKey(pin);
    if (!selectionKey || seenKeys.has(selectionKey)) {
      return;
    }

    seenKeys.add(selectionKey);
    uniquePins.push(pin);
  });

  return uniquePins;
};

const buildBoardPinTableRows = (pins) => (
  [...(Array.isArray(pins) ? pins : [])]
    .sort((leftPin, rightPin) => {
      const leftPinNumber = Number(leftPin?.pin_number);
      const rightPinNumber = Number(rightPin?.pin_number);
      const leftHasNumber = Number.isFinite(leftPinNumber);
      const rightHasNumber = Number.isFinite(rightPinNumber);

      if (leftHasNumber && rightHasNumber && leftPinNumber !== rightPinNumber) {
        return leftPinNumber - rightPinNumber;
      }

      if (leftHasNumber && !rightHasNumber) {
        return -1;
      }

      if (!leftHasNumber && rightHasNumber) {
        return 1;
      }

      return getBoardPinTableLabel(leftPin).localeCompare(getBoardPinTableLabel(rightPin));
    })
    .map((pin, index) => ({
      key: pin?.id ?? `${pin?.pin_number || 'pin'}-${pin?.pin_name || index}`,
      pin,
      selectionKey: getPinSelectionIdentityKey(pin),
      boardPin: getBoardPinTableLabel(pin),
      primaryFunction: getPlainTableText(pin?.functions) || '-',
      alternateFunction: getPlainTableText(pin?.alternate_functions) || '-',
      description: getPlainTableText(pin?.description) || '-',
    }))
);

const normalizePinsResponsePayload = (payload) => {
  const rawPins = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return rawPins.map((pin) => withPinCompatibilityDefaults(pin));
};

const safeParseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const parseStoredPinCallouts = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = safeParseJson(value);
    return Array.isArray(parsedValue) ? parsedValue : [];
  }

  return [];
};

const normalizePreviewBoardPayload = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const sourceSvgContent = value.svg_source_content || value.svgSourceContent || value.svg_content || '';

    return {
      ...value,
      gallery_images: normalizeBoardGalleryImages(value.gallery_images),
      affiliate_links: normalizeAffiliateLinks(value.affiliate_links),
      tutorial_urls: normalizeTutorialUrls(value.tutorial_urls),
      pin_callouts: parseStoredPinCallouts(value.pin_callouts ?? value.pinCallouts),
      svg_source_content: sourceSvgContent,
    svg_display_scale: value.svg_display_scale ?? value.svgDisplayScale ?? 1,
    svg_viewbox: normalizeSvgViewBox(value.svg_viewbox ?? value.svgViewBox),
    svg_rotation_deg: normalizeRotation(value.svg_rotation_deg ?? value.svgRotationDeg ?? 0),
    public_svg_rotation_deg: normalizeRotation(
      value.public_svg_rotation_deg ?? value.publicSvgRotationDeg ?? 0
    ),
    show_mcu_specs: Number(value.show_mcu_specs ?? value.showMcuSpecs ?? 1) ? 1 : 0,
  };
};

const buildBoardPreviewLabel = (board, hasPreviewToken) => {
  if (hasPreviewToken) {
    return 'Current draft preview';
  }

  return Number(board?.published)
    ? 'Published board preview'
    : 'Draft board preview';
};

const normalizeSimilarBoards = (value) => (
  Array.isArray(value)
    ? value
      .map((entry, index) => {
        const name = String(entry?.name || '').trim();
        const slug = String(entry?.slug || '').trim();
        const boardId = entry?.id ?? '';

        if (!name || (!slug && !boardId)) {
          return null;
        }

        const metaParts = [
          String(entry?.category || '').trim(),
          String(entry?.cpu || '').trim(),
          Number.isFinite(Number(entry?.pin_count)) && Number(entry?.pin_count) > 0
            ? `${Number(entry.pin_count)} pins`
            : '',
        ].filter(Boolean);

        return {
          id: boardId || `similar-${index}`,
          name,
          href: `/board/${encodeURIComponent(slug || boardId)}`,
          reason: formatSimilarBoardReason(entry?.reason, entry?.reasons),
          meta: metaParts.join(' / '),
          svgSrc: buildBoardSvgPreviewSrc(entry),
        };
      })
      .filter(Boolean)
    : []
);

const SimilarBoardSvgPreview = ({ svgSrc }) => {
  const [hasError, setHasError] = useState(false);

  if (!svgSrc || hasError) {
    return (
      <SimilarBoardCardIcon aria-hidden="true">
        <SimilarBoardCardIconFallback>
          <Cpu />
        </SimilarBoardCardIconFallback>
      </SimilarBoardCardIcon>
    );
  }

  return (
    <SimilarBoardCardIcon aria-hidden="true">
      <SimilarBoardCardIconImage
        src={svgSrc}
        alt=""
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        onError={() => setHasError(true)}
      />
    </SimilarBoardCardIcon>
  );
};

const BoardDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const [board, setBoard] = useState(null);
  const [pins, setPins] = useState([]);
  const [selectedPin, setSelectedPin] = useState(null);
  const [activeSvgSelection, setActiveSvgSelection] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [svgLoading, setSvgLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sidebarAd, setSidebarAd] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  const pinInfoRef = useRef(null);
  const pinoutRef = useRef(null);
  const shareFeedbackTimeoutRef = useRef(null);
  const selectedPinFlashSnapshotRef = useRef({ key: '', snapshots: [] });
  const previewToken = useMemo(
    () => new URLSearchParams(location.search).get('previewToken') || '',
    [location.search]
  );
  const isPreviewMode = useMemo(
    () => /\/boards?\/preview\//.test(location.pathname),
    [location.pathname]
  );
  const normalizedActiveFilters = useMemo(
    () => normalizePinFilterGroupList(activeFilters),
    [activeFilters]
  );
  const filterGroupOptions = PIN_FILTER_GROUPS;
  const customBoardCallouts = useMemo(
    () => (Array.isArray(board?.pin_callouts) ? board.pin_callouts : []).filter((callout) => {
      const calloutType = String(callout?.callout_type ?? callout?.calloutType ?? '').trim().toLowerCase();
      return calloutType === 'custom' || Boolean(String(callout?.custom_id ?? callout?.customId ?? '').trim());
    }),
    [board?.pin_callouts]
  );
  const selectedPinSelectionKey = useMemo(
    () => (selectedPin ? getPinSelectionIdentityKey(selectedPin) : ''),
    [selectedPin]
  );
  const selectedPinIsCustomCallout = useMemo(
    () => isCustomCalloutSelectionItem(selectedPin),
    [selectedPin]
  );
  const activeSvgSelectionType = String(activeSvgSelection?.type || '').trim();
  const activeSvgSelectionKey = String(activeSvgSelection?.key || '').trim();

  // Optional: safe CSS.escape fallback
  const cssEscape = (window.CSS && CSS.escape) ? CSS.escape : (s) => String(s).replace(/"/g, '\\"');
  const PIN_SELECTION_TARGETS = [
    '.pin-rect',
    '.pin-callout-line',
    '.pin-callout-bubble',
    '.pin-callout-anchor',
    '.micropinouts-alt-pin-callout-bubble',
    '.micropinouts-alt-pin-callout-text',
  ];
  const PIN_FLASH_INTERVAL_MS = 700;

  const hexToRgba = (color, alpha) => {
    if (typeof color !== 'string') {
      return `rgba(59, 130, 246, ${alpha})`;
    }

    const trimmed = color.trim();
    if (trimmed.startsWith('rgba(') || trimmed.startsWith('rgb(')) {
      return trimmed;
    }

    const normalized = trimmed.replace('#', '');
    if (!/^[0-9a-fA-F]{3,6}$/.test(normalized)) {
      return trimmed;
    }

    const expanded = normalized.length === 3
      ? normalized.split('').map((token) => token + token).join('')
      : normalized;

    const red = Number.parseInt(expanded.slice(0, 2), 16);
    const green = Number.parseInt(expanded.slice(2, 4), 16);
    const blue = Number.parseInt(expanded.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  };

  const parseSvgColor = (color) => {
    if (typeof color !== 'string') {
      return null;
    }

    const trimmed = color.trim();
    if (!trimmed || trimmed === 'none' || trimmed === 'transparent') {
      return null;
    }

    const rgbMatch = trimmed.match(/^rgba?\(\s*(\d{1,3}(?:\.\d+)?)\s*,\s*(\d{1,3}(?:\.\d+)?)\s*,\s*(\d{1,3}(?:\.\d+)?)(?:\s*,\s*[\d.]+\s*)?\)$/i);
    if (rgbMatch) {
      return {
        red: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[1])))),
        green: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[2])))),
        blue: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[3])))),
      };
    }

    const normalized = trimmed.replace('#', '');
    if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(normalized)) {
      return null;
    }

    const expanded = normalized.length === 3
      ? normalized.split('').map((token) => token + token).join('')
      : normalized;

    return {
      red: Number.parseInt(expanded.slice(0, 2), 16),
      green: Number.parseInt(expanded.slice(2, 4), 16),
      blue: Number.parseInt(expanded.slice(4, 6), 16),
    };
  };

  const getLighterCalloutFill = (color) => {
    const parsed = parseSvgColor(color);
    if (!parsed) {
      return 'rgb(201, 219, 254)';
    }

    const whiteMix = 0.72;
    const mixChannel = (channel) => Math.round(channel + ((255 - channel) * whiteMix));

    return `rgb(${mixChannel(parsed.red)}, ${mixChannel(parsed.green)}, ${mixChannel(parsed.blue)})`;
  };

  const getSvgColorValue = (element, property, fallback = '') => {
    if (!(element instanceof Element)) {
      return fallback;
    }

    const inlineValue = String(element.style?.getPropertyValue(property) || '').trim();
    if (inlineValue && inlineValue !== 'none' && inlineValue !== 'transparent') {
      return inlineValue;
    }

    const attributeValue = String(element.getAttribute(property) || '').trim();
    if (attributeValue && attributeValue !== 'none' && attributeValue !== 'transparent') {
      return attributeValue;
    }

    if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
      const computedValue = String(window.getComputedStyle(element).getPropertyValue(property) || '').trim();
      if (computedValue && computedValue !== 'none' && computedValue !== 'transparent') {
        return computedValue;
      }
    }

    return fallback;
  };

  const normalizeLegacyCalloutSelectionStyles = () => {
    document
      .querySelectorAll('style[data-micropinouts-interactive="1"]')
      .forEach((styleElement) => {
        const cssText = String(styleElement.textContent || '');
        const nextCssText = cssText.replace(
          /\.pin-callout-bubble\.selected\s*\{\s*fill:\s*rgba\(\s*0\s*,\s*0\s*,\s*255\s*,\s*0\.6\s*\)\s*!important;\s*\}/gi,
          '.pin-callout-bubble.selected { fill: var(--micropinouts-callout-selected-fill, rgba(201, 219, 254, 0.96)) !important; }'
        );

        if (nextCssText !== cssText) {
          styleElement.textContent = nextCssText;
        }
      });
  };

  const setSvgStyle = (element, property, value, priority = '') => {
    if (!element?.style) {
      return;
    }

    if (value === undefined || value === null || value === '') {
      element.style.removeProperty(property);
      return;
    }

    element.style.setProperty(property, value, priority);
  };

  const CALLOUT_OVERLAY_SELECTOR = [
    '[data-micropinouts-callout="1"]',
    '[data-micropinouts-alt-pin-callout="1"]',
    '[data-micropinouts-alt-pin-label="1"]',
  ].join(', ');

  const CALLOUT_ARTWORK_SAMPLE_POSITIONS = [
    [0.2, 0.2],
    [0.5, 0.2],
    [0.8, 0.2],
    [0.2, 0.5],
    [0.5, 0.5],
    [0.8, 0.5],
    [0.2, 0.8],
    [0.5, 0.8],
    [0.8, 0.8],
  ];

  const isSvgArtworkCandidate = (element, svgRoot) => {
    if (!(element instanceof Element) || !(svgRoot instanceof SVGElement)) {
      return false;
    }

    if (element === svgRoot || !svgRoot.contains(element)) {
      return false;
    }

    if (element.closest(CALLOUT_OVERLAY_SELECTOR)) {
      return false;
    }

    const tagName = String(element.tagName || '').toLowerCase();
    if (
      !tagName
      || tagName === 'style'
      || tagName === 'defs'
      || tagName === 'clippath'
      || tagName === 'mask'
      || tagName === 'marker'
      || tagName === 'lineargradient'
      || tagName === 'radialgradient'
      || tagName === 'filter'
    ) {
      return false;
    }

    if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
      const computedStyle = window.getComputedStyle(element);
      if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
        return false;
      }

      const opacity = Number.parseFloat(computedStyle.opacity || '1');
      if (Number.isFinite(opacity) && opacity <= 0) {
        return false;
      }
    }

    if (typeof window !== 'undefined' && typeof window.SVGGraphicsElement === 'function') {
      return element instanceof window.SVGGraphicsElement;
    }

    return true;
  };

  const doesCalloutOverlapBoardArtwork = (calloutGroup) => {
    if (!(calloutGroup instanceof Element) || typeof document === 'undefined') {
      return false;
    }

    const svgRoot = calloutGroup.ownerSVGElement;
    const bubble = calloutGroup.querySelector('rect.pin-callout-bubble[data-micropinouts-callout="1"]');

    if (!(svgRoot instanceof SVGElement) || !(bubble instanceof Element)) {
      return false;
    }

    const bubbleBounds = bubble.getBoundingClientRect();
    if (
      !Number.isFinite(bubbleBounds.width)
      || !Number.isFinite(bubbleBounds.height)
      || bubbleBounds.width < 6
      || bubbleBounds.height < 6
    ) {
      return false;
    }

    return CALLOUT_ARTWORK_SAMPLE_POSITIONS.some(([xRatio, yRatio]) => {
      const clientX = bubbleBounds.left + (bubbleBounds.width * xRatio);
      const clientY = bubbleBounds.top + (bubbleBounds.height * yRatio);
      const hitStack = document.elementsFromPoint(clientX, clientY);

      return hitStack.some((candidate) => isSvgArtworkCandidate(candidate, svgRoot));
    });
  };

  const syncCalloutSelectionVariables = () => {
    document
      .querySelectorAll('rect.pin-callout-bubble[data-micropinouts-callout="1"]')
      .forEach((bubble) => {
        const outlineColor = getSvgColorValue(bubble, 'stroke', '#475569');
        setSvgStyle(bubble, '--micropinouts-callout-selected-fill', getLighterCalloutFill(outlineColor));
      });
  };

  const applyPinVisualState = (element, { isCapabilityActive }) => {
    if (!(element instanceof Element)) {
      return;
    }

    setSvgStyle(element, 'fill', 'rgba(255, 255, 255, 0.95)');
    setSvgStyle(element, 'stroke', isCapabilityActive ? '#475569' : '#cbd5e1');
  };

  const findPinForSvgElement = (element) => {
    if (!(element instanceof Element)) {
      return null;
    }

    const svgId = String(element.getAttribute('data-svgid') || '').trim();
    const pinNumber = String(element.getAttribute('data-pin-number') || '').trim();
    const pinName = String(element.getAttribute('data-pin') || '').trim();

    return pins.find((pin) => {
      if (svgId && String(pin.svgId || pin.svg_id || '').trim() === svgId) {
        return true;
      }

      if (pinNumber && String(pin.pin_number || '').trim() === pinNumber) {
        return true;
      }

      return pinName && String(pin.pin_name || '').trim() === pinName;
    }) || null;
  };

  const findCustomCalloutForPayload = useCallback((payload = {}) => {
    const calloutKey = String(payload?.calloutKey ?? '').trim();
    const customId = String(payload?.customId ?? '').trim();

    return customBoardCallouts.find((callout) => {
      if (calloutKey && getCalloutLayoutKey(callout) === calloutKey) {
        return true;
      }

      if (customId && String(callout?.custom_id ?? callout?.customId ?? '').trim() === customId) {
        return true;
      }

      return false;
    }) || null;
  }, [customBoardCallouts]);

  const getPinFilterVisualState = (pin) => {
    const matchState = getPinFilterMatchState(pin, normalizedActiveFilters);
    return {
      isFilterActive: matchState.isActive,
      filterColor: matchState.color,
      calloutBaseColor: getPinCalloutBaseColor(pin),
    };
  };

  const getCustomCalloutFilterVisualState = (calloutGroup) => {
    return {
      isFilterActive: true,
      filterColor: '#475569',
      isFilterable: false,
    };
  };

  const applyCalloutVisualState = (calloutGroup, {
    isFilterActive,
    filterColor,
    hasActiveFilters,
    overlapsArtwork = false,
    calloutBaseColor = '',
  }) => {
    if (!(calloutGroup instanceof Element)) {
      return;
    }

    const rect = calloutGroup.querySelector('rect.pin-callout-bubble[data-micropinouts-callout="1"]');
    const line = calloutGroup.querySelector('.pin-callout-line');
    const text = calloutGroup.querySelector('.pin-callout-text');
    const anchor = calloutGroup.querySelector('.pin-callout-anchor');
    calloutGroup.setAttribute('data-micropinouts-over-artwork', overlapsArtwork ? '1' : '0');

    if (!hasActiveFilters) {
      calloutGroup.style.removeProperty('opacity');
      if (calloutBaseColor) {
        setSvgStyle(rect, 'fill', '');
        setSvgStyle(rect, 'stroke', calloutBaseColor);
        setSvgStyle(rect, '--micropinouts-callout-selected-fill', getLighterCalloutFill(calloutBaseColor));
        setSvgStyle(line, 'stroke', calloutBaseColor);
        setSvgStyle(text, 'fill', calloutBaseColor);
        setSvgStyle(anchor, 'stroke', calloutBaseColor);
        setSvgStyle(anchor, 'fill', '#ffffff');
        return;
      }

      setSvgStyle(rect, 'fill', '');
      setSvgStyle(rect, 'stroke', '');
      setSvgStyle(rect, '--micropinouts-callout-selected-fill', '');
      setSvgStyle(line, 'stroke', '');
      setSvgStyle(text, 'fill', '');
      setSvgStyle(anchor, 'stroke', '');
      setSvgStyle(anchor, 'fill', '');
      return;
    }

    const resolvedActiveColor = calloutBaseColor || filterColor;

    if (isFilterActive) {
      calloutGroup.style.setProperty('opacity', '1');
      setSvgStyle(rect, 'fill', hexToRgba(resolvedActiveColor, 0.12));
      setSvgStyle(rect, 'stroke', resolvedActiveColor);
      setSvgStyle(rect, '--micropinouts-callout-selected-fill', getLighterCalloutFill(resolvedActiveColor));
      setSvgStyle(line, 'stroke', resolvedActiveColor);
      setSvgStyle(text, 'fill', overlapsArtwork ? '#ffffff' : resolvedActiveColor);
      setSvgStyle(anchor, 'stroke', resolvedActiveColor);
      setSvgStyle(anchor, 'fill', '#ffffff');
      return;
    }

    calloutGroup.style.setProperty('opacity', '0.22');
    setSvgStyle(rect, 'fill', 'rgba(255, 255, 255, 0.92)');
    setSvgStyle(rect, 'stroke', '#94a3b8');
    setSvgStyle(line, 'stroke', '#94a3b8');
    setSvgStyle(text, 'fill', '#94a3b8');
    setSvgStyle(anchor, 'stroke', '#94a3b8');
    setSvgStyle(anchor, 'fill', '#ffffff');
  };

  const applySelectedCalloutVisualState = () => {
    document
      .querySelectorAll([
        'rect.pin-callout-bubble.selected[data-micropinouts-callout="1"]',
        'rect.micropinouts-alt-pin-callout-bubble.selected',
      ].join(', '))
      .forEach((bubble) => {
        const outlineColor = getSvgColorValue(bubble, 'stroke', '#475569');
        const calloutGroup = bubble.closest('g[data-micropinouts-callout="1"]');
        const line = calloutGroup?.querySelector('path.pin-callout-line');
        const text = bubble.matches('rect.micropinouts-alt-pin-callout-bubble')
          ? bubble.closest('g[data-micropinouts-alt-pin-callout="1"]')?.querySelector('text.micropinouts-alt-pin-callout-text')
          : calloutGroup?.querySelector('text.pin-callout-text');
        setSvgStyle(bubble, '--micropinouts-callout-selected-fill', getLighterCalloutFill(outlineColor));
        setSvgStyle(bubble, 'fill', getLighterCalloutFill(outlineColor), 'important');
        setSvgStyle(line, 'stroke', getLighterCalloutFill(outlineColor), 'important');
        setSvgStyle(text, 'fill', outlineColor);
      });
  };

  const getPinoutSvgScope = () => pinoutRef.current?.querySelector('[data-svg-artwork-root="1"]') || null;

  const getPinSelectionIdentity = (pinLike) => ({
    calloutKey: String(pinLike?.callout_key ?? pinLike?.calloutKey ?? '').trim(),
    customId: String(pinLike?.custom_id ?? pinLike?.customId ?? '').trim(),
    pinName: String(pinLike?.pin_name ?? pinLike?.pin ?? '').trim(),
    svgId: String(pinLike?.svgId ?? pinLike?.svg_id ?? '').trim(),
    pinNumber: String(pinLike?.pin_number ?? pinLike?.pinNumber ?? '').trim(),
  });

  const getPinSelectionKey = (pinLike) => {
    const { calloutKey, customId, pinName, svgId, pinNumber } = getPinSelectionIdentity(pinLike);
    if (isCustomCalloutSelectionItem(pinLike)) {
      return `custom::${calloutKey || customId || pinName}`;
    }

    return `${svgId}::${pinNumber}::${pinName}`;
  };

  const normalizeSelectionPins = (pinList) => dedupePinsBySelectionIdentity(
    (Array.isArray(pinList) ? pinList : []).filter(Boolean)
  );

  const getPinSelectionSelectors = (pinLike) => {
    const { calloutKey, customId, pinName, svgId, pinNumber } = getPinSelectionIdentity(pinLike);
    const selectors = [];

    if (isCustomCalloutSelectionItem(pinLike)) {
      const calloutSelectorValue = calloutKey || customId;
      if (calloutSelectorValue) {
        selectors.push(`.pin-callout-line[data-callout-key="${cssEscape(calloutSelectorValue)}"]`);
        selectors.push(`.pin-callout-bubble[data-callout-key="${cssEscape(calloutSelectorValue)}"]`);
        selectors.push(`.pin-callout-anchor[data-callout-key="${cssEscape(calloutSelectorValue)}"]`);
        selectors.push(`.pin-callout-anchor-hit[data-callout-key="${cssEscape(calloutSelectorValue)}"]`);
      }

      if (!calloutKey && customId) {
        selectors.push(`.pin-callout-line[data-custom-id="${cssEscape(customId)}"]`);
        selectors.push(`.pin-callout-bubble[data-custom-id="${cssEscape(customId)}"]`);
        selectors.push(`.pin-callout-anchor[data-custom-id="${cssEscape(customId)}"]`);
        selectors.push(`.pin-callout-anchor-hit[data-custom-id="${cssEscape(customId)}"]`);
      }

      return selectors;
    }

    if (svgId) {
      PIN_SELECTION_TARGETS.forEach((selector) => selectors.push(`${selector}[data-svgid="${cssEscape(svgId)}"]`));
    }
    if (pinName) {
      PIN_SELECTION_TARGETS.forEach((selector) => selectors.push(`${selector}[data-pin="${cssEscape(pinName)}"]`));
    }
    if (pinNumber) {
      PIN_SELECTION_TARGETS.forEach((selector) => selectors.push(`${selector}[data-pin-number="${cssEscape(pinNumber)}"]`));
    }

    return selectors;
  };

  const getPinSelectionElements = (pinLike, scope = getPinoutSvgScope()) => {
    if (!scope || typeof scope.querySelectorAll !== 'function') {
      return [];
    }

    const selectors = getPinSelectionSelectors(pinLike);
    if (selectors.length === 0) {
      return [];
    }

    return Array.from(scope.querySelectorAll(selectors.join(',')));
  };

  const getSelectionElements = (pinList, scope = getPinoutSvgScope()) => {
    if (!scope || typeof scope.querySelectorAll !== 'function') {
      return [];
    }

    const seenElements = new Set();
    const selectionElements = [];

    normalizeSelectionPins(pinList).forEach((pin) => {
      getPinSelectionElements(pin, scope).forEach((element) => {
        if (!(element instanceof Element) || seenElements.has(element)) {
          return;
        }

        seenElements.add(element);
        selectionElements.push(element);
      });
    });

    return selectionElements;
  };

  const clearPinSelectionClasses = (scope = getPinoutSvgScope()) => {
    if (!scope || typeof scope.querySelectorAll !== 'function') {
      return;
    }

    scope
      .querySelectorAll([
        '.pin-hole.selected',
        '.pin-rect.selected',
        '.pin-callout-line.selected',
        '.pin-callout-bubble.selected',
        '.pin-callout-anchor.selected',
        '.micropinouts-alt-pin-callout-bubble.selected',
        '.micropinouts-alt-pin-callout-text.selected',
      ].join(', '))
      .forEach((element) => element.classList.remove('selected'));
  };

  const captureSelectionSnapshot = (pinList, scope = getPinoutSvgScope()) => {
    const seenElements = new Set();
    const snapshots = [];

    getSelectionElements(pinList, scope).forEach((element) => {
      if (!(element instanceof Element) || seenElements.has(element)) {
        return;
      }

      seenElements.add(element);
      snapshots.push({
        element,
        style: element.getAttribute('style'),
      });

      if (!element.matches('rect.pin-callout-bubble[data-micropinouts-callout="1"]')) {
        return;
      }

      const text = element.closest('g[data-micropinouts-callout="1"]')?.querySelector('text.pin-callout-text');
      if (!(text instanceof Element) || seenElements.has(text)) {
        return;
      }

      seenElements.add(text);
      snapshots.push({
        element: text,
        style: text.getAttribute('style'),
      });
    });

    return snapshots;
  };

  const restorePinSelectionSnapshot = (snapshots = []) => {
    snapshots.forEach(({ element, style }) => {
      if (!(element instanceof Element)) {
        return;
      }

      if (style === null) {
        element.removeAttribute('style');
        return;
      }

      element.setAttribute('style', style);
    });
  };

  const restoreActivePinSelectionSnapshot = () => {
    restorePinSelectionSnapshot(selectedPinFlashSnapshotRef.current.snapshots);
    selectedPinFlashSnapshotRef.current = { key: '', snapshots: [] };
  };

  const setPinsSelectionActive = (pinList, isActive, scope = getPinoutSvgScope()) => {
    const selectionElements = getSelectionElements(pinList, scope);
    if (selectionElements.length === 0) {
      return;
    }

    selectionElements.forEach((element) => element.classList.toggle('selected', isActive));

    if (isActive) {
      normalizeLegacyCalloutSelectionStyles();
      syncCalloutSelectionVariables();
      applySelectedCalloutVisualState();
    }
  };

  const refreshActiveSvgSelectionVisualState = (selection) => {
    clearPinSelectionClasses();

    const selectionPins = normalizeSelectionPins(selection?.pins);
    if (selectionPins.length === 0) {
      selectedPinFlashSnapshotRef.current = { key: '', snapshots: [] };
      return;
    }

    selectedPinFlashSnapshotRef.current = {
      key: String(selection?.key || ''),
      snapshots: captureSelectionSnapshot(selectionPins),
    };

    setPinsSelectionActive(selectionPins, true);
  };

  const activateSvgSelection = ({ type, key, pins: selectionPins, infoPin = null, scrollInfoOnMobile = false } = {}) => {
    const normalizedPins = normalizeSelectionPins(selectionPins);
    if (normalizedPins.length === 0) {
      return;
    }

    setSelectedPin(infoPin || null);
    setActiveSvgSelection({
      type: String(type || '').trim(),
      key: String(key || '').trim(),
      pins: normalizedPins,
    });

    if (scrollInfoOnMobile && infoPin) {
      scrollToPinInfoOnMobile();
    }

    // Selection styling writes inline SVG colors. Restore the previous pin's
    // exact pre-selection styles before highlighting the next one.
    restoreActivePinSelectionSnapshot();
    clearPinSelectionClasses();
    filterSVGElements();
    syncCalloutSelectionVariables();
    refreshActiveSvgSelectionVisualState({
      key,
      pins: normalizedPins,
    });
  };

  const selectPin = (pin, { scrollInfoOnMobile = false } = {}) => {
    if (!pin) {
      return;
    }

    activateSvgSelection({
      type: isCustomCalloutSelectionItem(pin) ? 'custom-callout' : 'pin',
      key: getPinSelectionKey(pin),
      pins: [pin],
      infoPin: pin,
      scrollInfoOnMobile,
    });
  };

  const handleSVGPinClick = (arg) => {
    // If we received the new payload shape, use it
    let payload = (arg && typeof arg === 'object' && 'pin' in arg) ? arg : null;

    // Legacy fallback: arg was a DOM event
    if (!payload && arg && arg.target) {
      const t = arg.target;
      const el = t.closest
        ? t.closest('.pin-rect, .pin-hole, .pin-callout-line, .pin-callout-line-hit, .pin-callout-bubble, .pin-callout-text, .pin-callout-anchor, .pin-callout-anchor-hit, .micropinouts-alt-pin-callout-bubble, .micropinouts-alt-pin-callout-text')
        : null;
      if (el) {
        payload = {
          pin: el.getAttribute('data-pin') || '',
          svgId: el.getAttribute('data-svgid') || el.id || '',
          pinNumber: el.getAttribute('data-pin-number') || '',
          calloutKey: el.getAttribute('data-callout-key') || '',
          calloutType: el.getAttribute('data-callout-type') || '',
          customId: el.getAttribute('data-custom-id') || '',
        };
      }
    }

    if (!payload) return;

    if (String(payload.calloutType || '').trim().toLowerCase() === 'custom') {
      const matchedCustomCallout = findCustomCalloutForPayload(payload);
      if (matchedCustomCallout) {
        selectPin(createCustomCalloutSelectionItem(matchedCustomCallout), { scrollInfoOnMobile: true });
      }
      return;
    }

    const { pin: pinName, svgId, pinNumber } = payload;

    // Find the pin record in your pins array
    const pin = pins.find(p =>
      (pinName && String(p.pin_name ?? '').trim() === String(pinName).trim()) ||
      (svgId && String(p.svgId ?? p.svg_id ?? '').trim() === String(svgId).trim()) ||
      (pinNumber && String(p.pin_number ?? p.pinNumber ?? '').trim() === String(pinNumber).trim())
    );
    if (!pin) return;

    selectPin(pin, { scrollInfoOnMobile: true });
  };

  const handlePinTableRowSelect = (pin) => {
    selectPin(pin);
  };

  const handleQuickPinGuideRowSelect = (row) => {
    if (!row) {
      return;
    }

    activateSvgSelection({
      type: 'quick-guide',
      key: row.key,
      pins: row.relatedPins,
      infoPin: null,
    });
  };

  const handleCautionPinRowSelect = (row) => {
    if (!row?.pin) {
      return;
    }

    activateSvgSelection({
      type: 'caution-row',
      key: row.key,
      pins: [row.pin],
      infoPin: row.pin,
    });
  };

  const handlePinTableRowKeyDown = (event, onSelect) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onSelect();
  };


  // Function to filter SVG elements based on active capability-tag filters
  const filterSVGElements = () => {
    if (!board?.svg_content) return;
    
    // Get all pin-hole elements
    const pinElements = document.querySelectorAll('.pin-hole');
    const calloutGroups = document.querySelectorAll('g[data-micropinouts-callout="1"]');
    const hasActiveFilters = normalizedActiveFilters.length > 0;
    
    pinElements.forEach(element => {
      const pin = findPinForSvgElement(element);
      
      if (pin) {
        const { isFilterActive } = getPinFilterVisualState(pin);
        applyPinVisualState(element, { isCapabilityActive: isFilterActive });
        
        if (isFilterActive) {
          element.classList.remove('hidden');
        } else {
          element.classList.add('hidden');
        }
      }
    });

    calloutGroups.forEach((calloutGroup) => {
      const overlapsArtwork = doesCalloutOverlapBoardArtwork(calloutGroup);

      if (calloutGroup.getAttribute('data-callout-type') === 'custom') {
        const customCalloutState = getCustomCalloutFilterVisualState(calloutGroup);
        applyCalloutVisualState(calloutGroup, {
          isFilterActive: customCalloutState.isFilterActive,
          filterColor: customCalloutState.filterColor,
          hasActiveFilters: hasActiveFilters && customCalloutState.isFilterable,
          overlapsArtwork,
        });
        return;
      }

      const calloutIdentityElement = calloutGroup.querySelector('[data-group][data-pin], [data-group][data-pin-number], [data-group][data-svgid]');
      const pin = findPinForSvgElement(calloutIdentityElement);
      applyCalloutVisualState(calloutGroup, {
        ...(getPinFilterVisualState(pin)),
        hasActiveFilters,
        overlapsArtwork,
      });
    });
  };


  // Update SVG filtering when activeFilters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    restoreActivePinSelectionSnapshot();
    clearPinSelectionClasses();
    normalizeLegacyCalloutSelectionStyles();
    filterSVGElements();
    syncCalloutSelectionVariables();
    refreshActiveSvgSelectionVisualState(activeSvgSelection);
    const nextFrame = window.requestAnimationFrame(() => {
      restoreActivePinSelectionSnapshot();
      clearPinSelectionClasses();
      normalizeLegacyCalloutSelectionStyles();
      filterSVGElements();
      syncCalloutSelectionVariables();
      refreshActiveSvgSelectionVisualState(activeSvgSelection);
    });
    return () => window.cancelAnimationFrame(nextFrame);
  }, [normalizedActiveFilters, pins, board?.svg_content, activeSvgSelection]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Alternate the active SVG pin selection between its base and selected styles for a gentle flash effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!activeSvgSelection?.pins?.length) {
      return undefined;
    }

    const selectionKey = String(activeSvgSelection?.key || '');
    const selectionPins = normalizeSelectionPins(activeSvgSelection?.pins);
    let showSelectedState = true;

    const intervalId = window.setInterval(() => {
      const { key, snapshots } = selectedPinFlashSnapshotRef.current;
      if (key !== selectionKey) {
        return;
      }

      if (showSelectedState) {
        clearPinSelectionClasses();
        restorePinSelectionSnapshot(snapshots);
      } else {
        setPinsSelectionActive(selectionPins, true);
      }

      showSelectedState = !showSelectedState;
    }, PIN_FLASH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [activeSvgSelection, normalizedActiveFilters, pins, board?.svg_content]);
  /* eslint-enable react-hooks/exhaustive-deps */


  // Check if user is admin
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    setIsAdmin(!!token);
  }, []);

  useEffect(() => {
    let isCurrent = true;

    const fetchSidebarAd = async () => {
      try {
        const response = await adsAPI.getAll();
        const nextSidebarAd = findAdRecordBySlot(response.data, AD_SLOT_BOARD_DETAIL_SIDEBAR);

        if (!isCurrent) {
          return;
        }

        setSidebarAd(nextSidebarAd?.image_url ? nextSidebarAd : null);
      } catch (adError) {
        console.error('Error loading board detail sidebar ad:', adError);

        if (isCurrent) {
          setSidebarAd(null);
        }
      }
    };

    fetchSidebarAd();

    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    setActiveFilters([]);
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setSvgLoading(true);
      setBoard(null);
      setSelectedPin(null);
      setActiveSvgSelection(null);
      setError(null);

      if (isPreviewMode) {
        const draftPayload = previewToken ? readBoardPreviewDraft(previewToken, id) : null;

        if (draftPayload?.board) {
          setBoard(normalizePreviewBoardPayload(draftPayload.board));
          setPins(normalizePinsResponsePayload(draftPayload.pins));
          setLoading(false);
          setSvgLoading(false);
          return;
        }

        const { adminBoardsAPI } = await import('../services/adminApi');
        const [boardResponse, pinsResponse] = await Promise.all([
          adminBoardsAPI.getById(id),
          adminBoardsAPI.getPins(id),
        ]);

        setBoard(normalizePreviewBoardPayload(boardResponse.data));
        setPins(normalizePinsResponsePayload(pinsResponse.data));
        setLoading(false);
        setSvgLoading(false);
        return;
      }

      const [boardResponse, pinsResponse] = await Promise.all([
        boardsAPI.getSummary(id),
        boardsAPI.getPins(id)
      ]);
      
      setBoard(boardResponse.data);
      setPins(normalizePinsResponsePayload(pinsResponse.data));
      setLoading(false);

      try {
        const svgResponse = await boardsAPI.getSvg(id);
        setBoard((currentBoard) => (
          currentBoard
            ? {
                ...currentBoard,
                svg_content: svgResponse.data?.svg_content || '',
                svg_source_content: svgResponse.data?.svg_source_content || currentBoard.svg_source_content,
                svg_display_scale: svgResponse.data?.svg_display_scale ?? currentBoard.svg_display_scale,
                svg_rotation_deg: svgResponse.data?.svg_rotation_deg ?? currentBoard.svg_rotation_deg,
                public_svg_rotation_deg: svgResponse.data?.public_svg_rotation_deg ?? currentBoard.public_svg_rotation_deg,
                pin_callouts: Array.isArray(svgResponse.data?.pin_callouts)
                  ? svgResponse.data.pin_callouts
                  : currentBoard.pin_callouts,
              }
            : currentBoard
        ));
      } catch (svgError) {
        console.error('Error fetching board SVG:', svgError);
      } finally {
        setSvgLoading(false);
      }
    } catch (err) {
      setError(isPreviewMode ? 'Board preview is unavailable.' : 'Failed to load board data');
      console.error(isPreviewMode ? 'Error fetching board preview:' : 'Error fetching board data:', err);
      setLoading(false);
      setSvgLoading(false);
    }
  }, [id, isPreviewMode, previewToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => () => {
    if (shareFeedbackTimeoutRef.current) {
      window.clearTimeout(shareFeedbackTimeoutRef.current);
    }
  }, []);


  // Refetch data when page becomes visible (e.g., returning from edit page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchData();
      }
    };

    const handleFocus = () => {
      fetchData();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchData]);

  const toggleFilter = (tagValue) => {
    const normalizedTagValue = normalizePinFilterGroup(tagValue);
    if (!normalizedTagValue) {
      return;
    }

    setActiveFilters((previousFilters) => (
      previousFilters.includes(normalizedTagValue)
        ? previousFilters.filter((tag) => tag !== normalizedTagValue)
        : [...previousFilters, normalizedTagValue]
    ));
  };

  const galleryImages = normalizeBoardGalleryImages(board?.gallery_images);
  const affiliateLinks = normalizeAffiliateLinks(board?.affiliate_links);
  const tutorialUrls = normalizeTutorialUrls(board?.tutorial_urls);
  const similarBoards = useMemo(
    () => normalizeSimilarBoards(board?.similar_boards),
    [board?.similar_boards]
  );
  const quickPinGuideRows = useMemo(
    () => buildQuickPinGuideRows(pins),
    [pins]
  );
  const cautionPinRows = useMemo(
    () => buildCautionPinTableRows(pins),
    [pins]
  );
  const pinTableRows = useMemo(
    () => buildBoardPinTableRows(pins),
    [pins]
  );
  const showMcuSpecs = Number(board?.show_mcu_specs ?? 1) === 1;
  const supportedProtocols = useMemo(
    () => normalizeSupportedProtocols(board?.supported_protocols).sort((left, right) => {
      const leftKey = String(left ?? '').trim().toLowerCase();
      const rightKey = String(right ?? '').trim().toLowerCase();
      const leftIndex = PROTOCOL_SORT_ORDER[leftKey] ?? Number.MAX_SAFE_INTEGER;
      const rightIndex = PROTOCOL_SORT_ORDER[rightKey] ?? Number.MAX_SAFE_INTEGER;

      if (leftIndex !== rightIndex) {
        return leftIndex - rightIndex;
      }

      return formatSupportedProtocolLabel(left).localeCompare(formatSupportedProtocolLabel(right));
    }),
    [board?.supported_protocols]
  );
  const verificationStatus = normalizeBoardVerificationStatus(board?.verification_status);
  const verificationBadgeLabel = getBoardVerificationLabel(verificationStatus);
  const documentationUrl = String(board?.link || '').trim();
  const datasheetUrl = String(board?.datasheet_url || '').trim();
  const referenceEntries = useMemo(() => {
    const nextEntries = [];
    const boardName = String(board?.name || '').trim();

    if (documentationUrl) {
      nextEntries.push({
        key: 'documentation',
        type: 'documentation',
        title: 'Documentation',
        url: documentationUrl,
        boardName,
      });
    }

    if (datasheetUrl) {
      nextEntries.push({
        key: 'datasheet',
        type: 'datasheet',
        title: 'Datasheet',
        url: datasheetUrl,
        boardName,
      });
    }

    tutorialUrls.forEach((tutorialUrl, index) => {
      const inferredTitle = inferReferenceTitleFromUrl(tutorialUrl);

      nextEntries.push({
        key: `tutorial-${index}`,
        type: inferredTitle.toLowerCase().includes('guide') ? 'guide' : 'tutorial',
        title: inferredTitle || (tutorialUrls.length > 1 ? `Tutorial ${index + 1}` : 'Tutorial'),
        url: tutorialUrl,
        boardName,
      });
    });

    return nextEntries.map((reference) => ({
      ...reference,
      group: getReferenceGroup(reference),
      accentClass: getReferenceAccentClass(reference),
      description: getReferenceDescription(reference),
      domain: getReferenceDomain(reference.url),
      Icon: getReferenceIcon(reference),
    }));
  }, [board?.name, datasheetUrl, documentationUrl, tutorialUrls]);
  const referenceGroups = useMemo(
    () => (
      ['official', 'guides', 'generic']
        .map((groupKey) => ({
          key: groupKey,
          label: REFERENCE_GROUP_LABELS[groupKey],
          items: referenceEntries.filter((reference) => reference.group === groupKey),
        }))
        .filter((group) => group.items.length > 0)
    ),
    [referenceEntries]
  );
  const hasReferenceLinks = referenceEntries.length > 0;
  const seoDescription = deriveBoardSeoDescription(board);
  const seoKeywords = deriveBoardSeoKeywords(board);
  const canonicalBoardIdentifier = board?.slug || id;
  const canonicalBoardUrl = canonicalBoardIdentifier ? appUrl(`/board/${canonicalBoardIdentifier}`) : '';
  const previewLabel = isPreviewMode ? buildBoardPreviewLabel(board, Boolean(previewToken)) : '';
  const publicPinoutImageIdentifier = !isPreviewMode ? (board?.id || canonicalBoardIdentifier) : '';
  const publicBoardTitle = formatPublicBoardTitle(board?.name);
  const publicPinoutImageAlt = buildBoardPinoutImageAlt(publicBoardTitle || board?.name);
  const publicPinoutImageUrl = useMemo(() => {
    if (isPreviewMode) {
      return '';
    }

    return publicPinoutImageIdentifier ? buildBoardPinoutRasterPreviewSrc(board) : '';
  }, [board, isPreviewMode, publicPinoutImageIdentifier]);
  const publicPinoutSeoImageSource = useMemo(() => {
    if (isPreviewMode || !publicPinoutImageIdentifier) {
      return '';
    }

    return publicPinoutImageUrl || buildBoardSvgPreviewSrc(board);
  }, [board, isPreviewMode, publicPinoutImageIdentifier, publicPinoutImageUrl]);
  const publicPinoutSeoImageUrl = useMemo(() => {
    if (!publicPinoutSeoImageSource) {
      return '';
    }

    return appUrl(publicPinoutSeoImageSource);
  }, [publicPinoutSeoImageSource]);
  const calloutSourceSvg = board?.svg_source_content || board?.svg_content || '';
  const boardSvgRotation = useMemo(
    () => normalizeRotation(board?.svg_rotation_deg ?? 0),
    [board?.svg_rotation_deg]
  );
  const publicSvgRotation = useMemo(
    () => normalizeRotation(board?.public_svg_rotation_deg ?? 0),
    [board?.public_svg_rotation_deg]
  );
  const finalPublicRotation = useMemo(
    () => normalizeRotation(boardSvgRotation + publicSvgRotation),
    [boardSvgRotation, publicSvgRotation]
  );
  const storedViewBox = useMemo(
    () => (
      normalizeSvgViewBox(board?.svg_viewbox ?? board?.svgViewBox)
      || parseSvgViewBoxFromMarkup(board?.svg_content || calloutSourceSvg)
    ),
    [board?.svgViewBox, board?.svg_content, board?.svg_viewbox, calloutSourceSvg]
  );
  const publicPreviewCallouts = useMemo(() => {
    const normalizedCallouts = Array.isArray(board?.pin_callouts) ? board.pin_callouts : [];

    if (!calloutSourceSvg || finalPublicRotation === boardSvgRotation) {
      return normalizedCallouts;
    }

    return rotateCallouts(
      normalizedCallouts,
      calloutSourceSvg,
      boardSvgRotation,
      finalPublicRotation
    );
  }, [board?.pin_callouts, boardSvgRotation, calloutSourceSvg, finalPublicRotation]);
  const publicPreviewViewBox = useMemo(() => {
    if (!storedViewBox || !calloutSourceSvg || finalPublicRotation === boardSvgRotation) {
      return storedViewBox;
    }

    return rotateSvgViewBox(
      storedViewBox,
      calloutSourceSvg,
      boardSvgRotation,
      finalPublicRotation
    );
  }, [boardSvgRotation, calloutSourceSvg, finalPublicRotation, storedViewBox]);
  const publicPreviewSvg = useMemo(() => {
    if (!calloutSourceSvg) {
      return board?.svg_content || '';
    }

    return buildPinoutPreviewSvg(calloutSourceSvg, pins || [], publicPreviewCallouts, finalPublicRotation, {
      viewBoundsMode: 'board',
      lockedViewBox: publicPreviewViewBox || null,
      normalizeCalloutSize: false,
    });
  }, [
    board?.svg_content,
    calloutSourceSvg,
    finalPublicRotation,
    pins,
    publicPreviewCallouts,
    publicPreviewViewBox,
  ]);
  const boardStructuredData = useMemo(() => {
    if (isPreviewMode || !canonicalBoardUrl || !publicPinoutSeoImageUrl || !publicBoardTitle) {
      return null;
    }

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'TechArticle',
      headline: publicBoardTitle,
      description: seoDescription,
      image: [publicPinoutSeoImageUrl],
      url: canonicalBoardUrl,
      mainEntityOfPage: canonicalBoardUrl,
      publisher: {
        '@type': 'Organization',
        name: 'MicroPinouts',
        url: appUrl('/'),
      },
    };

    const updatedAt = String(board?.updated_at || '').trim();
    if (updatedAt) {
      structuredData.dateModified = updatedAt;
    }

    return structuredData;
  }, [
    board?.updated_at,
    canonicalBoardUrl,
    isPreviewMode,
    publicBoardTitle,
    publicPinoutSeoImageUrl,
    seoDescription,
  ]);

  usePageSeo({
    title: publicBoardTitle
      ? `${publicBoardTitle}${isPreviewMode ? ' Preview' : ''} | MicroPinouts`
      : isPreviewMode
        ? 'Board Preview | MicroPinouts'
        : '',
    description: seoDescription,
    keywords: seoKeywords,
    canonicalUrl: canonicalBoardUrl,
    imageUrl: publicPinoutSeoImageUrl,
  });
  useStructuredData('board-detail', boardStructuredData);

  const getBoardIcon = (boardName) => {
    if (boardName?.toLowerCase().includes('arduino') || boardName?.toLowerCase().includes('stm32')) return <Cpu size={32} />;
    if (boardName?.toLowerCase().includes('esp') || boardName?.toLowerCase().includes('nodemcu') || boardName?.toLowerCase().includes('wemos') ) return <Wifi size={32} />;
    return <Zap size={32} />;
  };

  // Embed functions
  const generateEmbedCode = () => {
    const embedUrl = appUrl(`/embed/board/${id}`);
    const iframeCode = `<iframe src="${embedUrl}" width="800" height="600" frameborder="0" style="border: 1px solid #e2e8f0; border-radius: 8px;"></iframe>`;
    setEmbedCode(iframeCode);
    setShowEmbedModal(true);
  };

  const copyEmbedCode = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy embed code:', err);
    }
  };

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      const didCopy = document.execCommand('copy');
      if (!didCopy) {
        throw new Error('Legacy clipboard copy failed.');
      }
    } finally {
      textArea.remove();
    }
  };

  const showShareFeedback = (status) => {
    setShareStatus(status);

    if (shareFeedbackTimeoutRef.current) {
      window.clearTimeout(shareFeedbackTimeoutRef.current);
    }

    shareFeedbackTimeoutRef.current = window.setTimeout(() => {
      setShareStatus('');
      shareFeedbackTimeoutRef.current = null;
    }, 2000);
  };

  const handleExportPng = async () => {
    const svgElement = pinoutRef.current?.querySelector('[data-svg-artwork-root="1"] > svg');
    if (!(svgElement instanceof SVGElement)) {
      return;
    }

    try {
      await downloadSvgElementAsPng(svgElement, {
        fileName: `${board?.name || 'board'}-pinout.png`,
      });
    } catch (error) {
      console.error('Failed to export board pinout as PNG:', error);
    }
  };

  const handleShareLink = async () => {
    const shareUrl = window.location.href;
    const shareTitle = publicBoardTitle || document.title || 'MicroPinouts';

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
        showShareFeedback('shared');
        return;
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }
      }
    }

    try {
      await copyTextToClipboard(shareUrl);
      showShareFeedback('copied');
    } catch (error) {
      console.error('Failed to share board link:', error);
    }
  };

  if (loading) {
    return (
      <BoardDetailContainer>
        <BoardDetailInner>
          <LoadingSpinner>
            <div className="loading-spinner"></div>
          </LoadingSpinner>
        </BoardDetailInner>
      </BoardDetailContainer>
    );
  }

  if (error || !board) {
    return (
      <BoardDetailContainer>
        <BoardDetailInner>
          <ErrorMessage>{error || 'Board not found'}</ErrorMessage>
        </BoardDetailInner>
      </BoardDetailContainer>
    );
  }

   const scrollToPinInfoOnMobile = () => {
      if (window.innerWidth > 768) {
        return;
      }

      window.requestAnimationFrame(() => {
        pinInfoRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    };

  return (
    <BoardDetailContainer>
      <BoardDetailInner>
        <BoardHeader>
          {previewLabel && (
            <PreviewStateRow>
              <PreviewStateChip>{previewLabel}</PreviewStateChip>
            </PreviewStateRow>
          )}
          <BoardTitle>
            {getBoardIcon(board.name)}
            {publicBoardTitle || board.name}
          </BoardTitle>
          <BoardSummaryGrid>
            <BoardSpecsColumn>
              <BoardSpecs>
                {board.package_type && (
                  <SpecItem>
                    <SpecLabel>Package</SpecLabel>
                    <SpecValue>{board.package_type}</SpecValue>
                  </SpecItem>
                )}

                {board.pin_count && (
                  <SpecItem>
                    <SpecLabel>Pins</SpecLabel>
                    <SpecValue>{board.pin_count}</SpecValue>
                  </SpecItem>
                )}

                {formatBoardVoltage(board) && (
                  <SpecItem>
                    <SpecLabel>Voltage</SpecLabel>
                    <SpecValue>{formatBoardVoltage(board)}</SpecValue>
                  </SpecItem>
                )}

                {showMcuSpecs && board.cpu && (
                  <SpecItem>
                    <SpecLabel>CPU</SpecLabel>
                    <SpecValue>{board.cpu}</SpecValue>
                  </SpecItem>
                )}

                {showMcuSpecs && board.clock_speed && (
                  <SpecItem>
                    <SpecLabel>Clock Speed</SpecLabel>
                    <SpecValue>{board.clock_speed}</SpecValue>
                  </SpecItem>
                )}

                {showMcuSpecs && board.flash_memory && (
                  <SpecItem>
                    <SpecLabel>Flash</SpecLabel>
                    <SpecValue>{board.flash_memory}</SpecValue>
                  </SpecItem>
                )}

                {showMcuSpecs && board.ram && (
                  <SpecItem>
                    <SpecLabel>RAM</SpecLabel>
                    <SpecValue>{board.ram}</SpecValue>
                  </SpecItem>
                )}

                {showMcuSpecs && board.rom && (
                  <SpecItem>
                    <SpecLabel>ROM</SpecLabel>
                    <SpecValue>{board.rom}</SpecValue>
                  </SpecItem>
                )}
              </BoardSpecs>
            </BoardSpecsColumn>
          </BoardSummaryGrid>
        </BoardHeader>

          <ContentGrid $hasSidebar={true}>
            <MainContentColumn>
            <PinoutSection ref={pinoutRef}>
              {publicPreviewSvg ? (
                <BoardPublicSvgPreview
                  svgContent={publicPreviewSvg}
                  pins={pins}
                  displayScale={board?.svg_display_scale}
                  mobileDisplayScaleAdjustment={0.75}
                  maxWidth={BOARD_DETAIL_PREVIEW_MAX_WIDTH}
                  showAlternatePinCalloutColors={true}
                  activeFilterGroups={normalizedActiveFilters}
                  onPinClick={handleSVGPinClick}
                />
              ) : (
                <SvgLoadingState>
                  {svgLoading ? 'Loading interactive board diagram...' : 'Board diagram unavailable.'}
                </SvgLoadingState>
              )}
            </PinoutSection>

              <DetailsSection>
                <PinoutTitle>
                  <Info size={24} />
                  About {board.name}
                </PinoutTitle>
                <BoardSubtitle dangerouslySetInnerHTML={{ __html: board.description }} />
              </DetailsSection>
              <SupportedProtocolsSection>
                <PinoutTitle>
                  <Check size={24} />
                  Supported Protocols
                </PinoutTitle>
                {supportedProtocols.length > 0 ? (
                  <ProtocolBadgeList>
                    {supportedProtocols.map((protocol) => {
                      const protocolKey = String(protocol ?? '').trim().toLowerCase();

                      return (
                        <ProtocolCheckBadge key={protocolKey || String(protocol)}>
                          <Check size={16} />
                          {formatSupportedProtocolLabel(protocol)}
                        </ProtocolCheckBadge>
                      );
                    })}
                  </ProtocolBadgeList>
                ) : (
                  <DetailsEmptyText>
                    No supported protocol metadata has been added for this board yet.
                  </DetailsEmptyText>
                )}
              </SupportedProtocolsSection>
              {quickPinGuideRows.length > 0 && (
                <QuickGuideSection>
                  <PinoutTitle>
                    <Zap size={24} />
                    Quick Pin Selection Guide
                  </PinoutTitle>
                  <SummaryTableScroll>
                    <QuickPinTable>
                      <thead>
                        <tr>
                          <th>Use case</th>
                          <th>Pins</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quickPinGuideRows.map((row) => (
                          <tr
                            key={row.key}
                            data-interactive="true"
                            data-selected={activeSvgSelectionType === 'quick-guide' && activeSvgSelectionKey === row.key ? 'true' : 'false'}
                            aria-selected={activeSvgSelectionType === 'quick-guide' && activeSvgSelectionKey === row.key}
                            tabIndex={0}
                            onClick={() => handleQuickPinGuideRowSelect(row)}
                            onKeyDown={(event) => handlePinTableRowKeyDown(event, () => handleQuickPinGuideRowSelect(row))}
                            title={`Flash ${row.useCase.toLowerCase()} pins in the diagram`}
                          >
                            <td data-label="Use case"><strong>{row.useCase}</strong></td>
                            <td data-label="Pins">{row.pins}</td>
                          </tr>
                        ))}
                      </tbody>
                    </QuickPinTable>
                  </SummaryTableScroll>
                </QuickGuideSection>
              )}
              {cautionPinRows.length > 0 && (
                <PinTableSection>
                  <PinoutTitle>
                    <AlertTriangle size={24} />
                    Pins to Use With Caution
                  </PinoutTitle>
                  <SummaryTableScroll>
                    <QuickPinTable>
                      <thead>
                        <tr>
                          <th>Pin</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cautionPinRows.map((row) => (
                          <tr
                            key={row.key}
                            data-interactive="true"
                            data-selected={activeSvgSelectionType === 'caution-row' && activeSvgSelectionKey === row.key ? 'true' : 'false'}
                            aria-selected={activeSvgSelectionType === 'caution-row' && activeSvgSelectionKey === row.key}
                            tabIndex={0}
                            onClick={() => handleCautionPinRowSelect(row)}
                            onKeyDown={(event) => handlePinTableRowKeyDown(event, () => handleCautionPinRowSelect(row))}
                            title={`Flash ${row.boardPin} in the diagram`}
                          >
                            <td data-label="Pin"><strong>{row.boardPin}</strong></td>
                            <td data-label="Reason">{row.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </QuickPinTable>
                  </SummaryTableScroll>
                </PinTableSection>
              )}
              {pinTableRows.length > 0 && (
                <PinTableSection>
                  <PinoutTitle>
                    <FileText size={24} />
                    {board.name} Pin Details
                  </PinoutTitle>
                  <MobileScrollHint>Swipe horizontally to view all pin details.</MobileScrollHint>
                  <PinTableScroll>
                    <DetailedPinTable>
                      <thead>
                        <tr>
                          <th>Board Pin</th>
                          <th>Primary Function</th>
                          <th>Alternate Function</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pinTableRows.map((row) => (
                          <tr
                            key={row.key}
                            data-interactive="true"
                            data-selected={row.selectionKey === selectedPinSelectionKey ? 'true' : 'false'}
                            aria-selected={row.selectionKey === selectedPinSelectionKey}
                            tabIndex={0}
                            onClick={() => handlePinTableRowSelect(row.pin)}
                            onKeyDown={(event) => handlePinTableRowKeyDown(event, () => handlePinTableRowSelect(row.pin))}
                            title={`Select ${row.boardPin}`}
                          >
                            <td><strong>{row.boardPin}</strong></td>
                            <td>{row.primaryFunction}</td>
                            <td>{row.alternateFunction}</td>
                            <td>{row.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </DetailedPinTable>
                  </PinTableScroll>
                </PinTableSection>
              )}
              {publicPinoutImageUrl && (
                <StaticPinoutDetails>
                  <StaticPinoutSummary>{board.name} Static Pinout Image</StaticPinoutSummary>
                  <StaticPinoutImageWrap>
                    <StaticPinoutImage
                      src={publicPinoutImageUrl}
                      alt={publicPinoutImageAlt}
                      decoding="async"
                    />
                  </StaticPinoutImageWrap>
                </StaticPinoutDetails>
              )}
              {galleryImages.length > 0 && (
                <GallerySection>
                  <PinoutTitle>
                    <Info size={24} />
                    Board Gallery
                  </PinoutTitle>
                  <GalleryGrid>
                    {galleryImages.map((image, index) => (
                      <GalleryLink
                        key={`gallery-card-${index}`}
                        href={resolveBoardGalleryImageSrc(image)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <GalleryImage
                          src={resolveBoardGalleryImageSrc(image)}
                          alt={getBoardGalleryImageCaption(image) || `${board.name} gallery ${index + 1}`}
                          loading="lazy"
                        />
                        <GalleryCaption>{getBoardGalleryImageCaption(image) || `Image ${index + 1}`}</GalleryCaption>
                      </GalleryLink>
                    ))}
                  </GalleryGrid>
                </GallerySection>
              )}
            </MainContentColumn>

            <Sidebar>
              <ActionButtonRow>
                <CompareButton
                  type="button"
                  onClick={() => setShowCompareModal(true)}
                  title="Compare board"
                  aria-label="Compare board"
                >
                  <GitCompare size={18} />
                </CompareButton>
                <ExportButton
                  type="button"
                  onClick={handleExportPng}
                  title="Export as PNG"
                  aria-label="Export as PNG"
                >
                  <Download size={18} />
                </ExportButton>
                <ShareButton
                  type="button"
                  onClick={handleShareLink}
                  title={shareStatus === 'shared' ? 'Link shared' : shareStatus === 'copied' ? 'Link copied' : 'Share link'}
                  aria-label={shareStatus === 'shared' ? 'Link shared' : shareStatus === 'copied' ? 'Link copied' : 'Share link'}
                >
                  {shareStatus ? <Check size={18} /> : <Share2 size={18} />}
                </ShareButton>
                <EmbedButton
                  type="button"
                  onClick={generateEmbedCode}
                  title="Embed board"
                  aria-label="Embed board"
                >
                  <Code size={18} />
                </EmbedButton>
                {isAdmin && (
                  <EditButton
                    to={`/admin/boards/${id}/edit`}
                    title="Edit board"
                    aria-label="Edit board"
                  >
                    <Edit3 size={18} />
                  </EditButton>
                )}
              </ActionButtonRow>

              <InfoPanel ref={pinInfoRef}>
                <PanelTitle>
                  <Info size={20} />
                  {selectedPinIsCustomCallout ? 'Callout Information' : 'Pin Information'}
                </PanelTitle>
                
                <AnimatePresence mode="wait">
                  {selectedPin ? (
                    <motion.div
                      key={selectedPinSelectionKey || selectedPin.id || selectedPin.pin_name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      <PinDetails>
                        <div className="pin-name">{selectedPin.pin_name}</div>
                        <div className="pin-number">
                          {selectedPinIsCustomCallout
                            ? 'Custom callout'
                            : `Pin ${selectedPin.pin_number}`}
                        </div>
                        
                        {selectedPin.functions && (
                          <div className="pin-functions">
                            <strong>Primary Function:</strong><br />
                            {selectedPin.functions}
                          </div>
                        )}
                        
                        {selectedPin.alternate_functions && (
                          <div className="pin-functions">
                            <strong>Alternate Functions:</strong><br />
                            {selectedPin.alternate_functions}
                          </div>
                        )}

                        <div className="pin-specs">
                          {formatPinVoltage(selectedPin) && (
                            <div className="spec-row">
                              <span className="spec-label">Voltage:</span>
                              <span className="spec-value">{formatPinVoltage(selectedPin)}</span>
                            </div>
                          )}
                          {formatPinCurrent(selectedPin) && (
                            <div className="spec-row">
                              <span className="spec-label">Current:</span>
                              <span className="spec-value">{formatPinCurrent(selectedPin)}</span>
                            </div>
                          )}
                          {getPinFilterGroups(selectedPin).length > 0 && (
                            <div className="spec-row">
                              <span className="spec-label">Capabilities:</span>
                              <span className="spec-value">
                                {getPinFilterGroups(selectedPin).map((capability) => (
                                  <PinGroupBadge key={capability} $groupValue={capability}>
                                    {getPinFilterGroupDefinition(capability)?.label || capability}
                                  </PinGroupBadge>
                                ))}
                              </span>
                            </div>
                          )}
                          {selectedPin.is_5v_tolerant ? (
                            <div className="spec-row">
                              <span className="spec-label">5V Logic:</span>
                              <span className="spec-value">Tolerant</span>
                            </div>
                          ) : null}
                        </div>
                        
                        {selectedPin.description && (
                          <div className="pin-description">
                            <strong>Description:</strong><br />
                            <div dangerouslySetInnerHTML={{ __html: selectedPin.description }} />
                          </div>
                        )}
                        {selectedPin.use_with_caution && selectedPin.caution_reason && (
                          <div className="pin-description">
                            <strong>Caution:</strong><br />
                            <div>{selectedPin.caution_reason}</div>
                          </div>
                        )}
                      </PinDetails>
                    </motion.div>
                  ) : (
                    <PinInfo>
                      Click a pin or custom callout to view detailed information
                    </PinInfo>
                  )}
                </AnimatePresence>
                <BackToDiagramButton
                  type="button"
                  onClick={() => {
                    pinoutRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }}
                >
                  Back to diagram
                </BackToDiagramButton>
              </InfoPanel>

              {sidebarAd?.image_url ? (
                <SponsoredAdCard>
                  <SponsoredAdHeader>
                    <SponsoredAdLabel>Sponsored</SponsoredAdLabel>
                  </SponsoredAdHeader>

                  {sidebarAd.click_url ? (
                    <SponsoredAdLink
                      href={sidebarAd.click_url}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      aria-label={sidebarAd.alt_text || 'Open sponsored board detail ad'}
                    >
                      <SponsoredAdImage
                        src={sidebarAd.image_url}
                        alt={sidebarAd.alt_text || 'Sponsored board detail ad'}
                        loading="lazy"
                      />
                    </SponsoredAdLink>
                  ) : (
                    <SponsoredAdImage
                      src={sidebarAd.image_url}
                      alt={sidebarAd.alt_text || 'Sponsored board detail ad'}
                      loading="lazy"
                    />
                  )}
                </SponsoredAdCard>
              ) : null}
              
              {filterGroupOptions.length > 0 && (
                <InfoPanel>
                  <FilterSection>
                    <FilterTitle>
                      <Filter size={20} />
                      Filter by Pin Group
                    </FilterTitle>
                    <FilterButtons>
                      {filterGroupOptions.map((filterGroupOption) => (
                        <FilterButton
                          key={filterGroupOption.value}
                          $active={normalizedActiveFilters.includes(filterGroupOption.value)}
                          $groupValue={filterGroupOption.value}
                          aria-pressed={normalizedActiveFilters.includes(filterGroupOption.value)}
                          onClick={() => toggleFilter(filterGroupOption.value)}
                        >
                          {filterGroupOption.label}
                        </FilterButton>
                      ))}
                    </FilterButtons>
                  </FilterSection>
                </InfoPanel>
              )}

              <ReferenceInfoPanel>
                <SidebarDetailHeader>
                  <SidebarDetailTitle>
                    <FileText size={20} />
                    References
                  </SidebarDetailTitle>
                  <VerificationBadge $status={verificationStatus}>
                    {verificationBadgeLabel}
                  </VerificationBadge>
                </SidebarDetailHeader>

                {hasReferenceLinks ? (
                  <SidebarDetailList>
                    {referenceGroups.map((referenceGroup) => (
                      <SidebarDetailGroup key={referenceGroup.key}>
                        <SidebarDetailGroupLabel>{referenceGroup.label}</SidebarDetailGroupLabel>
                        <SidebarDetailGroupCards>
                          {referenceGroup.items.map((reference) => {
                            const ReferenceIcon = reference.Icon;
                            const referenceAriaLabel = `${reference.title}. ${reference.description}. ${reference.domain || 'External reference'}. Opens in a new tab.`;

                            return (
                              <SidebarDetailCard
                                key={reference.key}
                                href={reference.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={reference.url}
                                aria-label={referenceAriaLabel}
                                $accent={reference.accentClass}
                              >
                                <SidebarDetailCardIconBlock $accent={reference.accentClass} aria-hidden="true">
                                  <ReferenceIcon size={18} />
                                </SidebarDetailCardIconBlock>
                                <SidebarDetailCardContent>
                                  <SidebarDetailCardLabel>{reference.title}</SidebarDetailCardLabel>
                                  <SidebarDetailCardDescription>{reference.description}</SidebarDetailCardDescription>
                                  <SidebarDetailCardUrl>{reference.domain || reference.url}</SidebarDetailCardUrl>
                                </SidebarDetailCardContent>
                                <SidebarDetailCardArrow aria-hidden="true">
                                  <ExternalLink size={16} />
                                </SidebarDetailCardArrow>
                              </SidebarDetailCard>
                            );
                          })}
                        </SidebarDetailGroupCards>
                      </SidebarDetailGroup>
                    ))}
                  </SidebarDetailList>
                ) : (
                  <SidebarDetailEmptyState>No reference links yet.</SidebarDetailEmptyState>
                )}
              </ReferenceInfoPanel>

              {similarBoards.length > 0 && (
                <InfoPanel>
                  <PanelTitle>
                    <Cpu size={20} />
                    Similar Boards
                  </PanelTitle>

                  <SimilarBoardsList>
                    {similarBoards.map((similarBoard) => (
                      <SimilarBoardCard key={similarBoard.id} to={similarBoard.href}>
                        <SimilarBoardSvgPreview svgSrc={similarBoard.svgSrc} />
                        <SimilarBoardCardContent>
                          <SimilarBoardName>{similarBoard.name}</SimilarBoardName>
                          {similarBoard.meta && (
                            <SimilarBoardMeta>{similarBoard.meta}</SimilarBoardMeta>
                          )}
                          {similarBoard.reason && (
                            <SimilarBoardReason>{similarBoard.reason}</SimilarBoardReason>
                          )}
                        </SimilarBoardCardContent>
                      </SimilarBoardCard>
                    ))}
                  </SimilarBoardsList>
                </InfoPanel>
              )}

              {affiliateLinks.length > 0 && (
                <InfoPanel>
                  <PanelTitle>
                    <ShoppingCart size={20} />
                    Where to Buy
                  </PanelTitle>

                  <AffiliateLinksList>
                    {affiliateLinks.map((affiliateLink, index) => (
                      <AffiliateLinkCard
                        key={`affiliate-link-${index}`}
                        href={affiliateLink.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <AffiliateLinkLabel>{affiliateLink.label}</AffiliateLinkLabel>
                        <AffiliateLinkUrl>{affiliateLink.url}</AffiliateLinkUrl>
                      </AffiliateLinkCard>
                    ))}
                  </AffiliateLinksList>
                </InfoPanel>
              )}
            </Sidebar>


          </ContentGrid>
      </BoardDetailInner>
      

      <CompareModal
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        currentBoard={board}
      />

      <AnimatePresence>
        {showEmbedModal && (
          <EmbedModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setShowEmbedModal(false)}
          >
            <EmbedModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <EmbedModalHeader>
                <EmbedModalTitle>Embed This Board</EmbedModalTitle>
                <EmbedModalClose onClick={() => setShowEmbedModal(false)}>
                  ×
                </EmbedModalClose>
              </EmbedModalHeader>
              
              <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                Copy the code below to embed this board's pinout diagram on your website:
              </p>
              
              <EmbedCodeContainer>
                <CopyButton 
                  onClick={copyEmbedCode}
                  className={copied ? 'copied' : ''}
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? 'Copied!' : 'Copy'}
                </CopyButton>
                <EmbedCode>{embedCode}</EmbedCode>
              </EmbedCodeContainer>
              
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Preview:</h4>
                <EmbedPreview>
                  <iframe 
                    src={appUrl(`/embed/board/${id}`)}
                    width="100%" 
                    height="400" 
                    frameBorder="0"
                    style={{ border: 'none' }}
                    title="Board Embed Preview"
                  />
                </EmbedPreview>
              </div>
            </EmbedModalContent>
          </EmbedModal>
        )}
      </AnimatePresence>
      <Copyright>
        <strong>&copy; 2026 micropinouts.com</strong><br />
        All rights reserved.<br />
        Educational use and community sharing encouraged under applicable open-source licenses.
      </Copyright>
    </BoardDetailContainer>
  );
};

export default BoardDetail;
